import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';
import { projectApi, salApi, extraApi, suspensionApi, leadApi, api, supplierApi } from '../lib/api';
import { 
  formatDate, formatDateTime, formatCurrency, formatPercentage, isZeroMoney, moneySign,
  projectStatusLabels, projectStatusColors,
  paymentStatusLabels, paymentStatusColors,
  milestoneStatusLabels, milestoneStatusColors,
  extraStatusLabels, extraStatusColors,
  suspensionStatusLabels, suspensionStatusColors,
  furnitureQuoteStatusLabels, furnitureQuoteStatusColors,
  furniturePaymentStatusLabels, furniturePaymentStatusColors,
  estimateSectionLabels,
  getBadgeClass 
} from '../lib/utils';
import { isExtraContractEffective, isExtraFurnitureOperational } from '../lib/extraEligibility';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ExtraVariazioniPanel } from '../components/project/ExtraVariazioniPanel';
import { ContractVariationsTab } from '../components/project/ContractVariationsTab';
import FatturazioneV2 from '../components/project/economic/FatturazioneV2';
import { 
  ArrowLeft, Save, Building2, Euro, TrendingUp, Plus,
  Download, Upload, FileText, AlertTriangle, CheckCircle,
  Clock, Package, Pause, RefreshCw, History, User, Truck,
  ShoppingCart, X, Receipt, CreditCard, Lock, Unlock, Sofa,
  Edit, Copy, Send, Calendar as CalendarIcon, Trash2, FileCheck,
  Ban, RotateCcw
} from 'lucide-react';
import {
  LayoutGrid, Hammer, FolderOpen, Wallet, ListChecks, Zap,
  MoreVertical, ChevronRight, MapPin, CheckCircle2
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import FurnitureQuoteForm from '../components/FurnitureQuoteForm';
import SiteLogPanel from '../components/SiteLogPanel';
import DocumentManager from '../components/DocumentManager';
import { ProjectSummaryCards, ProjectProgressCard, ProjectSiteActionsCard } from '../components/project';
import { VatRateCard } from '../components/shared/VatRateCard';
import ProjectFurnitureTab from '../components/project/ProjectFurnitureTab';
import ProjectEconomicTab from '../components/project/ProjectEconomicTab';
import ProjectEconomicTimelineTab from '../components/project/ProjectEconomicTimelineTab';
import ProjectSubcontractsTab from '../components/project/ProjectSubcontractsTab';
import SalItemTimelineModal from '../components/project/SalItemTimelineModal';
import SalItemProgressControl from '../components/project/SalItemProgressControl';
import SalMeasureDetail from '../components/project/SalMeasureDetail';
import VariationReviewCard from '../components/project/VariationReviewCard';
import SalApprovalPanel from '../components/project/SalApprovalPanel';
import DirectorOfWorksDialog from '../components/project/DirectorOfWorksDialog';
import ProjectManagerDialog from '../components/project/ProjectManagerDialog';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isAdministration, isTechnician, isProjectManager } = useAuth();
  const { registerGuard, unregisterGuard, setBlocking, attemptNavigation } = useNavigationGuard();
  
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('panoramica'); // V2 IA: landing = Panoramica
  const [macroArea, setMacroArea] = useState('panoramica'); // panoramica|lavori|economico|arredi|documenti
  const [contractSummary, setContractSummary] = useState(null); // compute_contract_summary (source-of-truth)
  const [lead, setLead] = useState(null);
  const [salItems, setSalItems] = useState([]);
  const [extras, setExtras] = useState([]);
  const [suspensions, setSuspensions] = useState([]);
  const [salSummary, setSalSummary] = useState(null);
  const [salReports, setSalReports] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [unlockStatus, setUnlockStatus] = useState(null); // Info sblocco milestone
  const [extraForniturePayments, setExtraForniturePayments] = useState([]); // Pagamenti Extra Forniture 50/50
  const [extraForniturePaymentsLoading, setExtraForniturePaymentsLoading] = useState(false);
  const [furnitureQuotes, setFurnitureQuotes] = useState([]);
  const [furnitureLoading, setFurnitureLoading] = useState(false);
  const [showFurnitureModal, setShowFurnitureModal] = useState(false);
  const [editingFurnitureQuote, setEditingFurnitureQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // SAL editing state - modifiche locali prima del salvataggio
  const [salEdits, setSalEdits] = useState({}); // { itemId: { completion_percentage, delivered, ordered, order_* } }
  const [hasSalChanges, setHasSalChanges] = useState(false);
  const [showSalHistoryDialog, setShowSalHistoryDialog] = useState(false);
  const [showSalReportsDialog, setShowSalReportsDialog] = useState(false);
  const [showPraticaDialog, setShowPraticaDialog] = useState(false);
  const [showDlDialog, setShowDlDialog] = useState(false);
  const [showPmDialog, setShowPmDialog] = useState(false);
  
  // Dialog per ordine forniture
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [orderingItem, setOrderingItem] = useState(null);
  const [orderForm, setOrderForm] = useState({
    order_supplier: '',
    order_number: '',
    order_date: '',
    order_notes: ''
  });
  
  // Multi-selezione per ordini bulk
  const [selectedSupplyItems, setSelectedSupplyItems] = useState(new Set());
  const [showBulkOrderDialog, setShowBulkOrderDialog] = useState(false);
  
  // Ref per tracciare lo stato iniziale del SAL
  const initialSalRef = useRef({});
  
  const [showExtraModal, setShowExtraModal] = useState(false);
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [showExtraAcceptModal, setShowExtraAcceptModal] = useState(false);
  const [selectedExtraForAccept, setSelectedExtraForAccept] = useState(null);
  const [extraSignedFile, setExtraSignedFile] = useState(null);
  const [uploadingExtraSigned, setUploadingExtraSigned] = useState(false);
  
  // Delete project modal (admin only)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // SAL Item Exclusion Modal
  const [showExcludeModal, setShowExcludeModal] = useState(false);
  const [excludingItem, setExcludingItem] = useState(null);
  const [excludeReason, setExcludeReason] = useState('');
  // R.5: nuovi campi payload stralcio
  const [excludeCategory, setExcludeCategory] = useState('');
  const [excludeEconomicEffect, setExcludeEconomicEffect] = useState('');
  const [excludeReplacementExtraId, setExcludeReplacementExtraId] = useState('');
  const [approvedExtrasForReplacement, setApprovedExtrasForReplacement] = useState([]);
  // R.5.2 §3 — Timeline modal
  const [timelineItemId, setTimelineItemId] = useState(null);
  const [excludingInProgress, setExcludingInProgress] = useState(false);
  
  // Extra Forniture Payments Actions
  const [efMarkingGoodsReady, setEfMarkingGoodsReady] = useState(null);
  const [efMarkingPaid, setEfMarkingPaid] = useState(null);
  const [showEfInvoiceDialog, setShowEfInvoiceDialog] = useState(false);
  const [selectedEfPayment, setSelectedEfPayment] = useState(null);
  const [efInvoiceForm, setEfInvoiceForm] = useState({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: ''
  });
  const [savingEfInvoice, setSavingEfInvoice] = useState(false);

  // Building Practice (Pratica Edilizia)
  const [buildingPractice, setBuildingPractice] = useState({
    required: true,
    type: null,
    number: '',
    date: '',
    municipality: '',
    technician: '',
    subject: '',
    invoice_notes: '',
    status: 'da_compilare'
  });
  const [buildingPracticeLoading, setBuildingPracticeLoading] = useState(false);
  const [showBuildingPracticeDialog, setShowBuildingPracticeDialog] = useState(false);
  const [savingBuildingPractice, setSavingBuildingPractice] = useState(false);
  const [siteActionsRefreshKey, setSiteActionsRefreshKey] = useState(0);
  
  // Modale richiesta extra (tecnico) - FORNITURE con appuntamento scelte
  const [showRequestExtraModal, setShowRequestExtraModal] = useState(false);
  const [requestExtraData, setRequestExtraData] = useState({
    extra_type: 'fornitura',
    extra_category: 'F', // Categoria fornitura: F, G, H
    title: '',
    notes: '',
    supplier_id: null, // STEP 4E.2 · link opzionale anagrafica Supplier
    // Appuntamento scelte (obbligatorio)
    choice_appointment_date: '',
    choice_appointment_time: '',
    choice_supplier_name: '',
    choice_supplier_address: '',
    choice_appointment_notes: ''
  });
  const [requestingExtra, setRequestingExtra] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]); // STEP 4E.2 picker fornitori
  
  const [newExtra, setNewExtra] = useState({
    extra_type: 'lavoro',
    description: '',
    quantity: 1,
    unit: 'nr',
    unit_price: 0,
    notes: ''
  });
  
  const [newSuspension, setNewSuspension] = useState({
    reason: '',
    notes: '',
    start_date: '',
    expected_end_date: ''
  });
  
  // Navigation Guard per SAL
  const guardId = 'project-sal-guard';
  
  const handleSaveSalForGuard = useCallback(async () => {
    if (hasSalChanges) {
      await handleSaveSalChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSalChanges]);
  
  useEffect(() => {
    registerGuard(guardId, {
      hasChanges: () => hasSalChanges,
      onSave: handleSaveSalForGuard
    });
    return () => unregisterGuard(guardId);
  }, [hasSalChanges, handleSaveSalForGuard, registerGuard, unregisterGuard]);
  
  useEffect(() => {
    setBlocking(hasSalChanges);
  }, [hasSalChanges, setBlocking]);

  // Helper function for delivery label className (replaces nested ternary)
  const getDeliveryLabelClass = (isDelivered, isOrdered, itemOrdered) => {
    if (isDelivered) return 'text-green-600 font-medium';
    if (!isOrdered && !itemOrdered) return 'text-[#DCDCDC]';
    return 'text-[#737373]';
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // V2: carica il riepilogo contrattuale authoritative (compute_contract_summary)
  useEffect(() => {
    let active = true;
    salApi.getContractSummary(id)
      .then((res) => { if (active) setContractSummary(res.data); })
      .catch(() => { if (active) setContractSummary(null); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, salItems, extras]);

  // Permesso economico authoritative (stessa fonte usata per la macro-area Economico)
  const canViewEconomic = isAdmin || isAdministration || isProjectManager;

  // STEP 4E.2 · carica il picker fornitori (safe projection) all'apertura della modale scelta
  useEffect(() => {
    if (!showRequestExtraModal) return;
    let active = true;
    supplierApi.picker({ limit: 300 })
      .then((res) => { if (active) setSupplierOptions(res.data?.items || []); })
      .catch(() => { if (active) setSupplierOptions([]); });
    return () => { active = false; };
  }, [showRequestExtraModal]);

  // Primo sotto-tab REALMENTE consentito al ruolo per ogni macro-area
  // (evita di atterrare su un tab non autorizzato → pagina vuota)
  const firstAllowedTab = (area) => {
    if (area === 'economico') return (isAdmin || isAdministration) ? 'economic' : 'payments';
    return { panoramica: 'panoramica', lavori: 'sal', arredi: 'furniture', documenti: 'documents' }[area] || 'panoramica';
  };

  // V2 navigation: naviga a una macro-area (con sotto-tab opzionale)
  const goToArea = (area, sub) => {
    setMacroArea(area);
    setActiveTab(sub || firstAllowedTab(area));
  };

  const loadData = async () => {
    try {
      const [projectRes, salRes, extrasRes, suspensionsRes, salSummaryRes] = await Promise.all([
        projectApi.getOne(id),
        salApi.getByProject(id),
        extraApi.getByProject(id),
        suspensionApi.getByProject(id),
        api.get(`/projects/${id}/sal-summary`).catch(() => ({ data: null })),
      ]);
      
      const projectData = projectRes.data;
      setProject(projectData);
      setSalItems(salRes.data || []);
      setExtras(extrasRes.data || []);
      setSuspensions(suspensionsRes.data || []);
      setSalSummary(salSummaryRes.data || null);
      
      // Load lead info - gestione graceful se mancante
      if (projectData.lead_id && !projectData.lead_missing) {
        try {
          const leadRes = await leadApi.getOne(projectData.lead_id);
          setLead(leadRes.data);
        } catch (leadError) {
          // Lead non trovato - usa dati dal progetto o placeholder
          console.warn('Lead non trovato:', projectData.lead_id);
          setLead({
            id: projectData.lead_id,
            first_name: projectData.client_name || 'Cliente',
            last_name: '',
            email: projectData.client_email || '',
            phone: projectData.client_phone || '',
            property_address: projectData.client_address || '',
            _missing: true
          });
        }
      } else if (projectData.lead_data) {
        // Usa lead_data embedded nel progetto
        setLead(projectData.lead_data);
      } else {
        // Nessun lead associato
        setLead({
          first_name: projectData.client_name || 'Cliente non associato',
          last_name: '',
          _missing: true
        });
      }
      
      // Load SAL reports
      try {
        const reportsRes = await fetch(`/api/projects/${id}/sal-reports`, {
          withCredentials: true
        });
        if (reportsRes.ok) {
          setSalReports(await reportsRes.json());
        }
      } catch (err) {
        // SAL reports not critical - log for debugging only in dev
        if (process.env.NODE_ENV === 'development') {
          console.debug('SAL reports not loaded:', err?.message);
        }
      }
      
      // Load payment milestones
      await loadMilestones();
      
      // Load Extra Forniture Payments (Cat. F, G, H) - 50/50
      await loadExtraForniturePayments();
      
      // Load furniture quotes
      await loadFurnitureQuotes();
      
      // Load building practice
      try {
        const bpRes = await projectApi.getBuildingPractice(id);
        if (bpRes.data) {
          setBuildingPractice(bpRes.data);
        }
      } catch (bpErr) {
        // Non critico - usa default
        console.debug('Building practice not loaded:', bpErr?.message);
      }
    } catch (error) {
      toast.error('Errore nel caricamento del progetto');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };
  
  // Carica milestones dal nuovo sistema
  const loadMilestones = async () => {
    try {
      setMilestonesLoading(true);
      const response = await fetch(`/api/projects/${id}/milestones`, {
        withCredentials: true
      });
      if (response.ok) {
        const data = await response.json();
        setMilestones(data);
      }
      
      // Carica anche info sblocco
      const unlockResponse = await fetch(`/api/projects/${id}/milestones/unlock-status`, {
        withCredentials: true
      });
      if (unlockResponse.ok) {
        const unlockData = await unlockResponse.json();
        setUnlockStatus(unlockData);
      }
    } catch (err) {
      // Milestones not critical - log for debugging only in dev
      if (process.env.NODE_ENV === 'development') {
        console.debug('Milestones not loaded:', err?.message);
      }
    } finally {
      setMilestonesLoading(false);
    }
  };
  
  // Carica pagamenti Extra Forniture (Cat. F, G, H) - 50/50
  const loadExtraForniturePayments = async () => {
    try {
      setExtraForniturePaymentsLoading(true);
      const response = await fetch(`/api/projects/${id}/extra-forniture-payments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setExtraForniturePayments(data);
      }
    } catch (err) {
      console.debug('Extra Forniture Payments not loaded:', err?.message);
    } finally {
      setExtraForniturePaymentsLoading(false);
    }
  };
  
  // Aggiorna stato milestone
  const handleUpdateMilestoneStatus = async (milestoneId, newStatus) => {
    try {
      const response = await fetch(`/api/projects/${id}/milestones/${milestoneId}`, {
        method: 'PUT',
        headers: {
          withCredentials: true,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) {
        let msg = "Errore nell'aggiornamento";
        try {
          const j = await response.json();
          if (j?.detail === 'MILESTONE_MUST_BE_INVOICED_BEFORE_PAYMENT') {
            msg = "Prima registra la fattura, poi l'incasso.";
          }
        } catch (_) { /* ignore parse error */ }
        throw new Error(msg);
      }
      
      toast.success('Stato milestone aggiornato');
      loadMilestones();
    } catch (error) {
      toast.error(error?.message || "Errore nell'aggiornamento");
    }
  };
  
  // Rigenera milestones (se cambiano i parametri del contratto)
  const handleRegenerateMilestones = async () => {
    // Verifica se ci sono milestone già contabilizzate
    const contabilizzate = milestones.filter(m => 
      m.status === 'fatturato' || m.status === 'incassato'
    );
    
    let confirmMsg = 'Rigenerare le milestone?\n\n';
    if (contabilizzate.length > 0) {
      confirmMsg += `⚠️ ATTENZIONE: ${contabilizzate.length} milestone già fatturate/incassate NON verranno modificate.\n`;
      confirmMsg += 'Solo le milestone non ancora contabilizzate saranno aggiornate.';
    } else {
      confirmMsg += 'Le milestone esistenti saranno aggiornate con i nuovi importi.';
    }
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await fetch(`/api/projects/${id}/milestones/generate`, {
        method: 'POST',
        withCredentials: true
      });
      
      if (!response.ok) throw new Error('Failed to regenerate');
      
      const result = await response.json();
      
      // Mostra messaggio con dettagli
      if (result.warning) {
        toast.warning(result.warning);
      }
      toast.success(result.message || 'Milestone rigenerate');
      loadMilestones();
    } catch (error) {
      toast.error('Errore nella rigenerazione');
    }
  };

  // Genera avviso di fattura PDF
  const handleGenerateInvoiceNotice = async (milestone) => {
    try {
      toast.loading('Generazione avviso in corso...');
      
      const response = await fetch(
        `/api/projects/${id}/milestones/${milestone.id}/invoice-notice`,
        {
          credentials: 'include'
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Errore nella generazione');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Avviso_Fattura_${project?.project_number?.replace('/', '-') || 'PRJ'}_Rata${milestone.order || '1'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('Avviso di fattura generato');
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || 'Errore nella generazione dell\'avviso');
    }
  };

  // ===== PREVENTIVI ARREDI =====
  const loadFurnitureQuotes = async () => {
    try {
      setFurnitureLoading(true);
      const response = await fetch(`/api/furniture-quotes?project_id=${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFurnitureQuotes(data);
      }
    } catch (err) {
      // Furniture quotes not critical - log for debugging only in dev
      if (process.env.NODE_ENV === 'development') {
        console.debug('Furniture quotes not loaded:', err?.message);
      }
    } finally {
      setFurnitureLoading(false);
    }
  };

  const handleUpdateFurnitureStatus = async (quoteId, field, value) => {
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}`, {
        method: 'PUT',
        headers: {
          withCredentials: true,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });
      
      if (!response.ok) throw new Error('Failed to update');
      
      toast.success('Preventivo aggiornato');
      loadFurnitureQuotes();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Duplica preventivo arredi
  const handleDuplicateFurnitureQuote = async (quoteId) => {
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/duplicate`, {
        method: 'POST',
        headers: {
          withCredentials: true
        }
      });
      
      if (!response.ok) throw new Error('Failed to duplicate');
      
      toast.success('Preventivo duplicato');
      loadFurnitureQuotes();
    } catch (error) {
      toast.error('Errore nella duplicazione');
    }
  };

  // Apri modal per modifica preventivo
  const handleEditFurnitureQuote = (quote) => {
    setEditingFurnitureQuote(quote);
    setShowFurnitureModal(true);
  };

  // Download PDF preventivo arredi - supporta modalità summary e detailed
  const handleDownloadFurniturePDF = async (quote, mode = 'detailed') => {
    try {
      const modeLabel = mode === 'summary' ? 'cliente' : 'completo';
      toast.loading(`Generazione PDF ${modeLabel}...`);
      
      const response = await fetch(
        `/api/furniture-quotes/${quote.id}/pdf?mode=${mode}`,
        {
          headers: {
            withCredentials: true
          }
        }
      );
      
      if (!response.ok) throw new Error('Errore generazione PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = mode === 'summary' ? '_cliente' : '_completo';
      a.download = `Preventivo_Arredi_${quote.quote_number?.replace('/', '-') || 'download'}${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success(`PDF ${modeLabel} scaricato`);
    } catch (error) {
      toast.dismiss();
      toast.error('Errore download PDF');
    }
  };

  // Callback dopo salvataggio preventivo
  const handleFurnitureQuoteSaved = (savedQuote) => {
    loadFurnitureQuotes();
    setEditingFurnitureQuote(null);
  };

  // Aggiorna SAL item localmente (senza salvare)
  const handleUpdateSalItemLocal = (itemId, field, value) => {
    setSalEdits(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
    setHasSalChanges(true);
  };
  
  // Ottieni il valore corrente (modificato o originale)
  const getSalItemValue = (item, field) => {
    if (salEdits[item.id] && salEdits[item.id][field] !== undefined) {
      return salEdits[item.id][field];
    }
    return item[field];
  };
  
  // Salva tutte le modifiche SAL
  const handleSaveSalChanges = async () => {
    if (!hasSalChanges || Object.keys(salEdits).length === 0) return;
    
    setSaving(true);
    try {
      const items = Object.entries(salEdits).map(([itemId, changes]) => ({
        id: itemId,
        completion_percentage: changes.completion_percentage,
        ordered: changes.ordered,
        order_supplier: changes.order_supplier,
        order_number: changes.order_number,
        order_date: changes.order_date,
        order_notes: changes.order_notes,
        delivered: changes.delivered
      }));
      
      const response = await fetch(`/api/projects/${id}/sal/bulk-update`, {
        method: 'PUT',
        headers: {
          withCredentials: true,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      const data = await response.json();
      toast.success(`SAL salvato - Lavori: ${formatPercentage(data.lavori_percentage)}, Forniture: ${formatPercentage(data.forniture_percentage)}`);
      setSalEdits({});
      setHasSalChanges(false);
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio SAL');
    } finally {
      setSaving(false);
    }
  };
  
  // Annulla modifiche SAL
  const handleCancelSalChanges = () => {
    setSalEdits({});
    setHasSalChanges(false);
  };

  // ==== SAL ITEM EXCLUSION ====
  
  // Apre modale per stralciare una voce (R.5)
  const handleOpenExcludeModal = async (item) => {
    setExcludingItem(item);
    setExcludeReason('');
    setExcludeCategory('');
    setExcludeEconomicEffect('');
    setExcludeReplacementExtraId('');
    setShowExcludeModal(true);
    // Precarica extras approvati del progetto per il caso "Sostituita da variante"
    try {
      const res = await extraApi.getByProject(id);
      const approved = (res.data || []).filter((e) => isExtraContractEffective(e));
      setApprovedExtrasForReplacement(approved);
    } catch (_) {
      setApprovedExtrasForReplacement([]);
    }
  };

  // Conferma stralcio voce SAL (R.5)
  const handleConfirmExclude = async () => {
    if (!excludingItem) return;
    if (!excludeCategory) {
      toast.error('Seleziona la categoria di stralcio');
      return;
    }
    if (!excludeReason.trim() || excludeReason.trim().length < 8) {
      toast.error('La nota è obbligatoria (min 8 caratteri)');
      return;
    }
    // Deriva economic_effect per le categorie che lo richiedono esplicitamente
    let economic_effect = excludeEconomicEffect || null;
    if (excludeCategory === 'sostituita_da_variante') {
      economic_effect = 'represented_by_variation';
      if (!excludeReplacementExtraId) {
        toast.error('Seleziona la variante approvata che sostituisce la lavorazione');
        return;
      }
    }
    if ((excludeCategory === 'errore_progettuale' || excludeCategory === 'altro') && !economic_effect) {
      toast.error('Seleziona l\'effetto economico esplicito');
      return;
    }

    const payload = {
      category: excludeCategory,
      reason: excludeReason.trim(),
      economic_effect,
      replacement_extra_id: excludeCategory === 'sostituita_da_variante' ? excludeReplacementExtraId : null,
    };

    setExcludingInProgress(true);
    try {
      await salApi.exclude(excludingItem.id, payload);
      toast.success(`Voce "${excludingItem.code}" stralciata`);
      setShowExcludeModal(false);
      setExcludingItem(null);
      setExcludeReason('');
      setExcludeCategory('');
      setExcludeEconomicEffect('');
      setExcludeReplacementExtraId('');
      loadData();
    } catch (error) {
      console.error('Stralcio error:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'object' && detail?.message ? detail.message : (detail || 'Errore durante lo stralcio');
      toast.error(msg);
    } finally {
      setExcludingInProgress(false);
    }
  };
  
  // Riattiva una voce SAL esclusa
  const handleReactivateItem = async (item) => {
    try {
      await salApi.reactivate(item.id);
      toast.success(`Voce "${item.code}" riattivata`);
      loadData(); // Ricarica dati progetto
    } catch (error) {
      console.error('Reactivate error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la riattivazione');
    }
  };
  
  // Verifica se l'utente può escludere voci (esclusi venditori arredo)
  const canExcludeSalItems = user && user.role !== 'furniture_sales';
  

  
  // Apre dialog per inserire dettagli ordine
  const handleOpenOrderDialog = (item) => {
    setOrderingItem(item);
    // Pre-popola con dati esistenti se presenti
    const existingEdit = salEdits[item.id] || {};
    setOrderForm({
      order_supplier: existingEdit.order_supplier || item.order_supplier || '',
      order_number: existingEdit.order_number || item.order_number || '',
      order_date: existingEdit.order_date || item.order_date || new Date().toISOString().split('T')[0],
      order_notes: existingEdit.order_notes || item.order_notes || ''
    });
    setShowOrderDialog(true);
  };
  
  // Conferma ordine
  const handleConfirmOrder = () => {
    if (!orderingItem) return;
    
    // Valida almeno il fornitore
    if (!orderForm.order_supplier.trim()) {
      toast.error('Inserisci il nome del fornitore');
      return;
    }
    
    setSalEdits(prev => ({
      ...prev,
      [orderingItem.id]: {
        ...prev[orderingItem.id],
        ordered: true,
        order_supplier: orderForm.order_supplier,
        order_number: orderForm.order_number,
        order_date: orderForm.order_date,
        order_notes: orderForm.order_notes
      }
    }));
    setHasSalChanges(true);
    setShowOrderDialog(false);
    setOrderingItem(null);
    toast.success('Ordine registrato - ricorda di salvare');
  };
  
  // Annulla ordine (rimuove flag ordinato)
  const handleCancelOrder = (itemId) => {
    setSalEdits(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        ordered: false,
        order_supplier: null,
        order_number: null,
        order_date: null,
        order_notes: null
      }
    }));
    setHasSalChanges(true);
  };

  // ===== MULTI-SELEZIONE ORDINI BULK =====
  
  // Filtra solo le forniture (categorie F, G, H) non ancora ordinate
  const getSelectableSupplyItems = () => {
    if (!salItems || salItems.length === 0) return [];
    return salItems.filter(item => {
      const isSupply = ['F', 'G', 'H'].includes(item.category);
      const isOrdered = getSalItemValue(item, 'ordered');
      return isSupply && !isOrdered;
    });
  };

  // Toggle selezione singolo elemento
  const toggleSupplyItemSelection = (itemId) => {
    setSelectedSupplyItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Seleziona/deseleziona tutti
  const toggleSelectAllSupplyItems = () => {
    const selectableItems = getSelectableSupplyItems();
    if (selectedSupplyItems.size === selectableItems.length) {
      // Deseleziona tutti
      setSelectedSupplyItems(new Set());
    } else {
      // Seleziona tutti
      setSelectedSupplyItems(new Set(selectableItems.map(item => item.id)));
    }
  };

  // Apri dialog ordine bulk
  const handleOpenBulkOrderDialog = () => {
    if (selectedSupplyItems.size === 0) {
      toast.error('Seleziona almeno un elemento');
      return;
    }
    setOrderForm({
      order_supplier: '',
      order_number: '',
      order_date: new Date().toISOString().split('T')[0],
      order_notes: ''
    });
    setShowBulkOrderDialog(true);
  };

  // Conferma ordine bulk
  const handleConfirmBulkOrder = () => {
    if (selectedSupplyItems.size === 0) return;
    
    if (!orderForm.order_supplier.trim()) {
      toast.error('Inserisci il nome del fornitore');
      return;
    }
    
    // Applica l'ordine a tutti gli elementi selezionati
    const updates = {};
    selectedSupplyItems.forEach(itemId => {
      updates[itemId] = {
        ...salEdits[itemId],
        ordered: true,
        order_supplier: orderForm.order_supplier,
        order_number: orderForm.order_number,
        order_date: orderForm.order_date,
        order_notes: orderForm.order_notes
      };
    });
    
    setSalEdits(prev => ({ ...prev, ...updates }));
    setHasSalChanges(true);
    setShowBulkOrderDialog(false);
    setSelectedSupplyItems(new Set());
    toast.success(`Ordine registrato per ${Object.keys(updates).length} elementi - ricorda di salvare`);
  };

  const handleGenerateSalReport = async () => {
    // Se ci sono modifiche non salvate, chiedi prima di salvare
    if (hasSalChanges) {
      toast.error('Salva le modifiche SAL prima di generare il report');
      return;
    }
    
    try {
      toast.loading('Generazione Report SAL in corso...');
      
      const response = await fetch(`/api/projects/${id}/sal-report`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Errore nella generazione');
      }
      
      const data = await response.json();
      
      // Se abbiamo un download_url, scarica il PDF
      if (data.download_url) {
        const pdfResponse = await fetch(data.download_url, {
          credentials: 'include'
        });
        
        if (!pdfResponse.ok) throw new Error('Errore nel download del PDF');
        
        const blob = await pdfResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Report_SAL_${data.report_number || project?.project_number || 'report'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      
      toast.dismiss();
      toast.success(data.message || 'Report SAL generato e scaricato');
      loadData();
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || 'Errore nella generazione del report');
    }
  };

  const handleSyncExtras = async () => {
    try {
      const response = await fetch(`/api/projects/${id}/sal/sync-extras`, {
        method: 'POST',
        headers: {
          withCredentials: true,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to sync extras');
      
      const data = await response.json();
      if (data.items_added > 0) {
        toast.success(`${data.items_added} voci extra aggiunte al SAL`);
      } else {
        toast.info('Nessun nuovo extra da sincronizzare');
      }
      loadData();
    } catch (error) {
      toast.error('Errore nella sincronizzazione');
    }
  };

  const handleUpdateMilestone = async (index, newStatus) => {
    try {
      await projectApi.updateMilestone(id, index, newStatus);
      toast.success('Stato pagamento aggiornato');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleCreateExtra = async (e) => {
    e.preventDefault();
    try {
      await extraApi.create({
        project_id: id,
        ...newExtra,
        quantity: parseFloat(newExtra.quantity),
        unit_price: parseFloat(newExtra.unit_price)
      });
      
      toast.success('Extra creato - In attesa di approvazione admin');
      setShowExtraModal(false);
      setNewExtra({
        extra_type: 'lavoro',
        description: '',
        quantity: 1,
        unit: 'nr',
        unit_price: 0,
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Errore nella creazione dell\'extra');
    }
  };

  // Tecnico richiede extra - invia notifica all'amministrazione
  const handleRequestExtra = async (e) => {
    e.preventDefault();
    if (!requestExtraData.title.trim()) {
      toast.error('Inserisci una descrizione per la richiesta');
      return;
    }
    if (!requestExtraData.choice_appointment_date || !requestExtraData.choice_appointment_time) {
      toast.error('Inserisci data e ora dell\'appuntamento');
      return;
    }
    if (!requestExtraData.choice_supplier_name.trim()) {
      toast.error('Inserisci il nome del fornitore');
      return;
    }
    
    setRequestingExtra(true);
    try {
      // Combina data e ora per creare datetime ISO
      const appointmentDateTime = new Date(`${requestExtraData.choice_appointment_date}T${requestExtraData.choice_appointment_time}`);
      
      await extraApi.request({
        project_id: id,
        extra_type: requestExtraData.extra_type,
        extra_category: requestExtraData.extra_category, // Categoria lavoro C, D, E, S
        title: requestExtraData.title.trim(),
        notes: requestExtraData.notes.trim() || null,
        supplier_id: requestExtraData.supplier_id || null,
        choice_appointment_date: appointmentDateTime.toISOString(),
        choice_supplier_name: requestExtraData.choice_supplier_name.trim(),
        choice_supplier_address: requestExtraData.choice_supplier_address.trim() || null,
        choice_appointment_notes: requestExtraData.choice_appointment_notes.trim() || null
      });
      
      toast.success('Scelta cliente creata con appuntamento! Email inviate a tecnico, cliente e amministrazione.');
      setShowRequestExtraModal(false);
      setRequestExtraData({
        extra_type: 'fornitura',
        extra_category: 'F',
        title: '',
        notes: '',
        supplier_id: null,
        choice_appointment_date: '',
        choice_appointment_time: '',
        choice_supplier_name: '',
        choice_supplier_address: '',
        choice_appointment_notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della richiesta');
    } finally {
      setRequestingExtra(false);
    }
  };

  const handleApproveExtra = async (extraId) => {
    try {
      await extraApi.update(extraId, { approval_status: 'approvato_admin' });
      toast.success('Extra approvato');
      loadData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const openExtraAcceptModal = (extra) => {
    setSelectedExtraForAccept(extra);
    setExtraSignedFile(null);
    setShowExtraAcceptModal(true);
  };

  const handleExtraSignedUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Verifica dimensione (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File troppo grande (max 10MB)');
        return;
      }
      setExtraSignedFile(file);
    }
  };

  const handleClientAcceptExtra = async () => {
    if (!selectedExtraForAccept || !extraSignedFile) {
      toast.error('Carica il documento firmato');
      return;
    }
    
    setUploadingExtraSigned(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result;
          
          // Update extra with signed PDF and status
          await extraApi.update(selectedExtraForAccept.id, { 
            approval_status: 'accettato_cliente',
            signed_pdf_url: base64  // In produzione usare Object Storage
          });
          
          toast.success('Extra accettato dal cliente - Totale progetto aggiornato');
          setShowExtraAcceptModal(false);
          setSelectedExtraForAccept(null);
          setExtraSignedFile(null);
          loadData();
        } catch (error) {
          console.error('Error accepting extra:', error);
          toast.error(error.response?.data?.detail || 'Errore nell\'accettazione');
        } finally {
          setUploadingExtraSigned(false);
        }
      };
      reader.readAsDataURL(extraSignedFile);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Errore nella lettura del file');
      setUploadingExtraSigned(false);
    }
  };

  // ========== EXTRA FORNITURE PAYMENTS ACTIONS ==========
  
  // Mark goods ready (sblocca saldo)
  const handleEfMarkGoodsReady = async (paymentId) => {
    setEfMarkingGoodsReady(paymentId);
    try {
      const response = await fetch(`/api/extra-forniture-payments/${paymentId}/mark-goods-ready`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore');
      }
      toast.success('Saldo sbloccato - merce pronta');
      loadExtraForniturePayments();
    } catch (error) {
      toast.error(error.message || 'Errore durante lo sblocco');
    } finally {
      setEfMarkingGoodsReady(null);
    }
  };

  // Open invoice dialog
  const handleOpenEfInvoiceDialog = (payment) => {
    setSelectedEfPayment(payment);
    setEfInvoiceForm({
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: ''
    });
    setShowEfInvoiceDialog(true);
  };

  // Save invoice
  const handleSaveEfInvoice = async () => {
    if (!efInvoiceForm.invoice_number) {
      toast.error('Inserisci il numero fattura');
      return;
    }
    setSavingEfInvoice(true);
    try {
      const response = await fetch(`/api/extra-forniture-payments/${selectedEfPayment.id}/mark-invoiced`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          invoice_number: efInvoiceForm.invoice_number,
          invoice_date: efInvoiceForm.invoice_date,
          due_date: efInvoiceForm.due_date || null
        })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore');
      }
      toast.success('Fattura registrata');
      setShowEfInvoiceDialog(false);
      loadExtraForniturePayments();
    } catch (error) {
      toast.error(error.message || 'Errore durante il salvataggio');
    } finally {
      setSavingEfInvoice(false);
    }
  };

  // Mark as paid
  const handleEfMarkPaid = async (paymentId) => {
    setEfMarkingPaid(paymentId);
    try {
      const response = await fetch(`/api/extra-forniture-payments/${paymentId}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          payment_date: new Date().toISOString().split('T')[0]
        })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore');
      }
      toast.success('Pagamento segnato come incassato');
      loadExtraForniturePayments();
    } catch (error) {
      toast.error(error.message || 'Errore durante l\'incasso');
    } finally {
      setEfMarkingPaid(null);
    }
  };

  // Download avviso fattura Extra Forniture
  const handleDownloadEfAvviso = async (e, payment) => {
    e.stopPropagation();
    try {
      toast.loading('Generazione avviso in corso...');
      const response = await fetch(`/api/extra-forniture-payments/${payment.id}/invoice-notice`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Errore download');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Avviso_ExtraForniture_${payment.extra_number}_${payment.trigger_type?.includes('acconto') ? 'Acconto' : 'Saldo'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success('Avviso scaricato');
    } catch (error) {
      toast.dismiss();
      toast.error('Errore download PDF');
    }
  };

  const handleCreateSuspension = async (e) => {
    e.preventDefault();
    try {
      await suspensionApi.create({
        project_id: id,
        ...newSuspension,
        start_date: new Date(newSuspension.start_date).toISOString(),
        expected_end_date: newSuspension.expected_end_date 
          ? new Date(newSuspension.expected_end_date).toISOString() 
          : null
      });
      
      toast.success('Richiesta sospensione inviata');
      setShowSuspensionModal(false);
      setNewSuspension({
        reason: '',
        notes: '',
        start_date: '',
        expected_end_date: ''
      });
      loadData();
    } catch (error) {
      toast.error('Errore nella richiesta');
    }
  };

  const handleApproveSuspension = async (suspensionId, approved) => {
    try {
      await suspensionApi.update(suspensionId, {
        status: approved ? 'approvata' : 'respinta'
      });
      toast.success(approved ? 'Sospensione approvata' : 'Sospensione respinta');
      loadData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleUpdateProjectStatus = async (newStatus) => {
    try {
      await projectApi.update(id, { status: newStatus });
      toast.success('Stato progetto aggiornato');
      loadData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Delete project (admin only)
  const handleDeleteProject = async () => {
    if (deleteConfirmText !== project?.project_number) {
      toast.error('Inserisci il numero progetto corretto per confermare');
      return;
    }
    
    setDeleting(true);
    try {
      await projectApi.delete(id);
      toast.success(`Progetto ${project.project_number} eliminato`);
      navigate('/projects');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Group SAL items by category (A, B, C, etc.) instead of section
  // Separiamo LAVORI EDILI (C,D,E,S) e FORNITURE/MATERIALI (F,G,H)
  // ESCLUDIAMO A (Progettazione) e B (Pratiche) dal SAL Lavori
  const fornitureCategories = ['F', 'G', 'H'];
  const excludeFromLavori = ['A', 'B']; // Progettazione e Pratiche non fanno parte del SAL Lavori
  
  const lavoriItems = salItems.filter(item => 
    !fornitureCategories.includes(item.category) && 
    !excludeFromLavori.includes(item.category)
  );
  const fornitureItems = salItems.filter(item => fornitureCategories.includes(item.category));
  
  const groupedLavoriItems = lavoriItems.reduce((acc, item) => {
    const category = item.category || 'Altro';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});
  
  const groupedFornitureItems = fornitureItems.reduce((acc, item) => {
    const category = item.category || 'Altro';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  // STEP 3D.8B — badge Extra effective vs non-effective (SOLO display, mirror di
  // is_extra_contract_effective; NIENTE cambio eligibility/business). Dati affidabili dalla lista `extras`.
  const extrasById = {};
  (extras || []).forEach(e => { if (e && e.id) extrasById[e.id] = e; });
  const isExtraEffective = (extra) => {
    if (!extra) return false;
    const s = (extra.approval_status || extra.status || '').toLowerCase();
    const EFF = ['firmato_cliente','in_attesa_approvazione','in_validazione','validato','in_attesa_ordine','ordinato','completato','accettato_cliente','firmato','signed','final','definitivo'];
    const TERM = ['annullato','rifiutato','rifiutato_dal_cliente'];
    if (TERM.includes(s)) return false;
    if (EFF.includes(s)) return true;
    return !!(extra.signed_at || extra.signed_pdf_url);
  };

  // Category labels
  const categoryLabels = {
    'A': 'A - PROGETTAZIONE',
    'B': 'B - PRATICHE',
    'C': 'C - OPERE EDILI',
    'D': 'D - IMPIANTI ELETTRICI',
    'E': 'E - IMPIANTI IDRAULICI, TERMICI E CLIMATIZZAZIONE',
    'F': 'F - FINITURE',
    'G': 'G - SERRAMENTI',
    'H': 'H - PRODOTTI',
    'S': 'S - SICUREZZA',
    'Altro': 'Altre voci'
  };

  // Calculate SAL totals - separati per lavori e forniture
  const lavoriTotalValue = lavoriItems.reduce((sum, item) => sum + (item.total_value || 0), 0);
  const lavoriCompletedValue = lavoriItems.reduce((sum, item) => {
    const pct = getSalItemValue(item, 'completion_percentage') || 0;
    return sum + ((item.total_value || 0) * pct / 100);
  }, 0);
  const lavoriPercentage = lavoriTotalValue > 0 ? (lavoriCompletedValue / lavoriTotalValue) * 100 : 0;

  // V1: applica il coefficiente contrattuale PER SINGOLO PROGETTO alle voci lavori (contratto base).
  // Non tocca listini, preventivi, altri progetti. Extra restano separati.
  const contractCoefficient = (salSummary && typeof salSummary.contract_coefficient === 'number')
    ? salSummary.contract_coefficient
    : 1;
  const lavoriTotalValueNet = salSummary?.contract_base_amount ?? lavoriTotalValue;
  const lavoriCompletedValueNet = lavoriCompletedValue * contractCoefficient;
  
  // Forniture: calcoliamo in base a consegnato sì/no
  const fornitureTotalValue = fornitureItems.reduce((sum, item) => sum + (item.total_value || 0), 0);
  const fornitureDeliveredValue = fornitureItems.reduce((sum, item) => {
    const delivered = getSalItemValue(item, 'delivered');
    return sum + (delivered ? (item.total_value || 0) : 0);
  }, 0);
  const forniturePercentage = fornitureTotalValue > 0 ? (fornitureDeliveredValue / fornitureTotalValue) * 100 : 0;

  // Calculate extras totals (solo forniture, i lavori sono già nel SAL)
  const extrasFornitureTotal = (extras || [])
    .filter(e => isExtraFurnitureOperational(e))
    .reduce((sum, e) => sum + (e.total || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!project) return null;

  // Helper per ottenere il nome cliente
  const getClientName = () => {
    if (lead && (lead.first_name || lead.last_name)) {
      const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
      return name || 'Cliente non associato';
    }
    return 'Cliente non associato';
  };

  // Indirizzo cantiere effettivo: read-model canonico dal backend (stesso resolver della Projects card)
  const getSiteAddress = () => project?.effective_site_address || null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      {/* Header - Cliente come titolo principale */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" className="mt-1" onClick={() => {
            if (attemptNavigation('/projects')) {
              navigate('/projects');
            }
          }}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <div className="min-w-0 flex-1">
            {/* Titolo principale: Nome Cliente */}
            <h1 className={`text-2xl sm:text-3xl font-bold text-[#3C3C3C] ${lead?._missing ? 'text-amber-600 italic' : ''}`}>
              {getClientName()}
              {lead?._missing && <span className="text-sm font-normal ml-2">(non trovato)</span>}
            </h1>
            
            {/* Sottotitolo: Indirizzo cantiere effettivo (CASE E: incoerenza esplicita) */}
            {project?.site_address_unavailable ? (
              <p className="text-base text-amber-600 italic mt-1" data-testid="project-detail-site-address-unavailable">
                Indirizzo cantiere non disponibile
              </p>
            ) : getSiteAddress() && (
              <p className="text-base text-[#737373] mt-1" data-testid="project-detail-site-address">
                {getSiteAddress()}
              </p>
            )}
            
            {/* Meta info: Codice progetto + Badge stato */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="text-sm font-medium text-[#737373]">
                {project.project_number || 'Progetto senza codice'}
              </span>
              <span className="text-[#DCDCDC]">·</span>
              <span className={getBadgeClass(projectStatusColors[project.status])}>
                {projectStatusLabels[project.status]}
              </span>
            </div>
            {/* Responsabili */}
            {(project.project_manager_name || project.director_of_works_name_snapshot || project.technician_name) && (
              <div className="flex items-center gap-x-3 gap-y-1 mt-1.5 flex-wrap text-xs text-[#8a8a8a]" data-testid="project-responsabili">
                {project.project_manager_name && <span><span className="text-[#b0b0b0]">PM</span> {project.project_manager_name}</span>}
                {project.director_of_works_name_snapshot && <span><span className="text-[#b0b0b0]">DL</span> {project.director_of_works_name_snapshot}</span>}
                {project.technician_name && <span><span className="text-[#b0b0b0]">Tecnico</span> {project.technician_name}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 sm:mt-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="project-actions-menu">
                Azioni <MoreVertical className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {(isAdmin || isProjectManager || isAdministration) && (
                <DropdownMenuItem onClick={() => setShowDlDialog(true)} data-testid="action-dl">
                  <User className="w-4 h-4 mr-2" /> Direttore Lavori
                </DropdownMenuItem>
              )}
              {(isAdmin || isAdministration) && (
                <DropdownMenuItem onClick={() => setShowPmDialog(true)} data-testid="action-pm">
                  <User className="w-4 h-4 mr-2" /> Project Manager
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setShowSuspensionModal(true)} data-testid="action-suspend">
                <Pause className="w-4 h-4 mr-2" /> Richiedi sospensione
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => goToArea('lavori', 'suspensions')} data-testid="action-view-suspensions">
                <ListChecks className="w-4 h-4 mr-2" /> Sospensioni cantiere
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowDeleteModal(true)} data-testid="delete-project-btn"
                    className="text-red-600 focus:text-red-700 focus:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-2" /> Elimina progetto
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Warning Banner for Missing References */}
      {project.has_missing_references && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="text-amber-600 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-amber-800">Attenzione: Riferimenti mancanti</h4>
              <p className="text-sm text-amber-700 mt-1">
                {project.missing_references?.includes('lead') && 'Il cliente associato non è più presente nel sistema. '}
                {project.missing_references?.includes('estimate') && 'Il preventivo associato non è più presente nel sistema. '}
                {project.missing_references?.includes('technician') && 'Il tecnico assegnato non è più presente nel sistema. '}
              </p>
              <p className="text-xs text-amber-600 mt-2">
                Contatta l'amministratore per riassociare i dati corretti.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ===== V2 NAVIGAZIONE A MACRO-AREE ===== */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div data-testid="project-macro-nav" className="border-b border-[#ECECEC]">
          <div className="flex gap-1 flex-wrap">
            {[
              { key: 'panoramica', label: 'Panoramica', icon: LayoutGrid, show: true },
              { key: 'lavori', label: 'Lavori', icon: Hammer, show: true },
              { key: 'economico', label: 'Economico', icon: Euro, show: isAdmin || isAdministration || isProjectManager },
              { key: 'arredi', label: 'Arredi', icon: Sofa, show: true },
              { key: 'documenti', label: 'Documenti', icon: FolderOpen, show: true },
            ].filter(m => m.show).map(m => {
              const Icon = m.icon;
              const active = macroArea === m.key;
              return (
                <button key={m.key} data-testid={`macro-${m.key}`} onClick={() => goToArea(m.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${active ? 'border-[#FF7819] text-[#FF7819]' : 'border-transparent text-[#737373] hover:text-[#171717]'}`}>
                  <Icon className="w-4 h-4" /> {m.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sotto-navigazione LAVORI */}
        {macroArea === 'lavori' && (
          <div className="flex gap-1 flex-wrap" data-testid="sub-nav-lavori">
            {[
              { label: 'Avanzamento', tab: 'sal', show: true },
              { label: 'Variazioni', tab: 'extras', show: true },
              { label: 'Subappalti', tab: 'subcontracts', show: isAdmin || isProjectManager },
              { label: 'Diario', tab: 'sitelog', show: true },
            ].filter(s => s.show).map(s => (
              <button key={s.tab} data-testid={`sub-${s.tab}`} onClick={() => setActiveTab(s.tab)}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${activeTab === s.tab ? 'bg-[#FF7819] text-white' : 'bg-[#F5F5F5] text-[#525252] hover:bg-[#EDEDED]'}`}>
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* Sotto-navigazione ECONOMICO */}
        {macroArea === 'economico' && (
          <div className="flex gap-1 flex-wrap" data-testid="sub-nav-economico">
            {[
              { label: 'Riepilogo', tab: 'economic', show: isAdmin || isAdministration },
              { label: 'Fatturazione', tab: 'payments', show: true },
              { label: 'Timeline', tab: 'economic-timeline', show: isAdmin || isProjectManager || isAdministration },
            ].filter(s => s.show).map(s => (
              <button key={s.tab} data-testid={`sub-${s.tab}`} onClick={() => setActiveTab(s.tab)}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${activeTab === s.tab ? 'bg-[#FF7819] text-white' : 'bg-[#F5F5F5] text-[#525252] hover:bg-[#EDEDED]'}`}>
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* ===== PANORAMICA ===== */}
        <TabsContent value="panoramica" className="space-y-6" data-testid="panoramica-content">
          {/* Riga 1 — KPI (KPI economici gated con la stessa permission della macro-area Economico) */}
          <div className={canViewEconomic ? "grid grid-cols-2 md:grid-cols-5 gap-3" : "grid grid-cols-2 md:grid-cols-3 gap-3"} data-testid="panoramica-kpi">
            {/* SAL Lavori — SAL contrattuale authoritative (project.sal_percentage), sempre visibile */}
            <div className="rounded-xl border border-[#E4E4E4] border-l-4 border-l-[#FF7819] bg-white shadow-sm p-4">
              <p className="text-sm font-medium text-[#525252]">SAL Lavori</p>
              <p className="text-2xl font-semibold text-[#FF7819] mt-0.5 leading-tight">{formatPercentage(project.sal_percentage || 0)}</p>
              <p className="text-xs text-[#737373] mt-1">SAL contrattuale ufficiale</p>
            </div>
            {canViewEconomic ? (
              <>
                <div className="rounded-xl border border-[#E4E4E4] border-l-4 border-l-indigo-500 bg-white shadow-sm p-4">
                  <p className="text-sm font-medium text-[#525252]">Valore contrattuale aggiornato</p>
                  <p className="text-2xl font-semibold text-indigo-600 mt-0.5 leading-tight">{formatCurrency(contractSummary?.current_contract_value_computed ?? project.contract_amount ?? 0)}</p>
                  <p className="text-xs text-[#737373] mt-1">
                    Contratto originario {formatCurrency(contractSummary?.initial_contract_value ?? project.contract_amount ?? 0)}
                    {contractSummary && (() => {
                      const initial = contractSummary.initial_contract_value ?? project.contract_amount ?? 0;
                      const current = contractSummary.current_contract_value_computed ?? initial;
                      const net = Math.round((current - initial) * 100) / 100;
                      return <> · Variazioni nette {moneySign(net)}{formatCurrency(Math.abs(net))}</>;
                    })()}
                  </p>
                </div>
                <div className="rounded-xl border border-[#E4E4E4] border-l-4 border-l-[#0078C8] bg-white shadow-sm p-4">
                  <p className="text-sm font-medium text-[#525252]">Fatturato</p>
                  <p className="text-2xl font-semibold text-[#0078C8] mt-0.5 leading-tight">{formatCurrency((milestones || []).filter(m => ['fatturato', 'incassato'].includes(m.status)).reduce((s, m) => s + (m.amount || 0), 0))}</p>
                </div>
                <div className="rounded-xl border border-[#E4E4E4] border-l-4 border-l-emerald-500 bg-white shadow-sm p-4">
                  <p className="text-sm font-medium text-[#525252]">Incassato</p>
                  <p className="text-2xl font-semibold text-emerald-700 mt-0.5 leading-tight">{formatCurrency((milestones || []).filter(m => m.status === 'incassato').reduce((s, m) => s + (m.amount || 0), 0))}</p>
                </div>
                <div className="rounded-xl border border-[#E4E4E4] border-l-4 border-l-amber-500 bg-white shadow-sm p-4">
                  <p className="text-sm font-medium text-[#525252]">Prossima milestone</p>
                  {(() => {
                    const next = (milestones || []).filter(m => m.status !== 'incassato').sort((a, b) => (a.order || 0) - (b.order || 0))[0];
                    return next
                      ? <><p className="text-base font-semibold text-[#171717] mt-0.5 leading-tight truncate">{next.name}</p><p className="text-xs text-[#737373] mt-1">{formatCurrency(next.amount)} · {milestoneStatusLabels[next.status] || next.status}</p></>
                      : <p className="text-base text-[#a3a3a3] mt-0.5">—</p>;
                  })()}
                </div>
              </>
            ) : (
              <>
                {/* Ruolo senza accesso economico: KPI tecnici (nessun valore economico) */}
                <div className="rounded-xl border border-[#E4E4E4] bg-white shadow-sm p-4">
                  <p className="text-sm font-medium text-[#525252]">Avanzamento Lavori</p>
                  <p className="text-2xl font-semibold text-[#171717] mt-0.5 leading-tight">{formatPercentage(lavoriPercentage)}</p>
                  <p className="text-xs text-[#737373] mt-1">Completamento voci a listino</p>
                </div>
                <div className="rounded-xl border border-[#E4E4E4] bg-white shadow-sm p-4">
                  <p className="text-sm font-medium text-[#525252]">Avanzamento Forniture</p>
                  <p className="text-2xl font-semibold text-[#0078C8] mt-0.5 leading-tight">{fornitureItems.length > 0 ? formatPercentage(forniturePercentage) : '—'}</p>
                  <p className="text-xs text-[#737373] mt-1">Consegne materiali</p>
                </div>
              </>
            )}
          </div>

          {/* Riga 2 — Variazioni (economico, gated) + Stato cantiere */}
          <div className={canViewEconomic ? "grid grid-cols-1 lg:grid-cols-2 gap-4" : "grid grid-cols-1 gap-4"}>
            {canViewEconomic && (
            <div className="rounded-xl border border-[#E4E4E4] bg-white shadow-sm p-4" data-testid="panoramica-variazioni">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[15px] font-semibold text-[#171717]">Valore contrattuale</h4>
                <button onClick={() => goToArea('lavori', 'extras')} className="text-[13px] text-[#FF7819] flex items-center gap-1 hover:underline" data-testid="goto-variazioni">
                  Vai alle variazioni <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-[14px] text-[#525252]">Contratto originario</span><span className="text-[15px] font-medium text-[#171717]">{formatCurrency(contractSummary?.initial_contract_value ?? project.contract_amount ?? 0)}</span></div>
                <div className="flex justify-between"><span className="text-[14px] text-[#525252]">Variazioni in aumento</span><span className="text-[15px] font-semibold text-emerald-700">{isZeroMoney((contractSummary?.positive_variations || 0) + (contractSummary?.variation_positive_total || 0)) ? '' : '+'}{formatCurrency((contractSummary?.positive_variations || 0) + (contractSummary?.variation_positive_total || 0))}</span></div>
                <div className="flex justify-between"><span className="text-[14px] text-[#525252]">Variazioni in diminuzione</span><span className="text-[15px] font-semibold text-red-600">{isZeroMoney((contractSummary?.standalone_stralci_total || 0) + (contractSummary?.negative_variations_other_than_stralci || 0) + (contractSummary?.variation_negative_total || 0)) ? '' : '−'}{formatCurrency((contractSummary?.standalone_stralci_total || 0) + (contractSummary?.negative_variations_other_than_stralci || 0) + (contractSummary?.variation_negative_total || 0))}</span></div>
                <div className="flex justify-between border-t border-[#E4E4E4] pt-1.5"><span className="text-[14px] font-medium text-[#171717]">Valore aggiornato</span><span className="text-[16px] font-bold text-[#FF7819]">{formatCurrency(contractSummary?.current_contract_value_computed ?? project.contract_amount ?? 0)}</span></div>
              </div>
            </div>
            )}
            <div className="rounded-xl border border-[#E4E4E4] bg-white shadow-sm p-4" data-testid="panoramica-stato-cantiere">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[15px] font-semibold text-[#171717]">Stato cantiere</h4>
                <button onClick={() => goToArea('lavori', 'sitelog')} className="text-[13px] text-[#FF7819] flex items-center gap-1 hover:underline" data-testid="goto-attivita">
                  Vedi attività <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                <span className="text-[13px] text-[#737373]">Stato</span><span className="text-[14px] font-semibold text-[#171717] text-right">{projectStatusLabels[project.status]}</span>
                {project.start_date && (<><span className="text-[13px] text-[#737373]">Data inizio</span><span className="text-[14px] font-semibold text-[#171717] text-right">{formatDate(project.start_date)}</span></>)}
                <span className="text-[13px] text-[#737373]">Pratica edilizia</span><span className="text-[14px] font-semibold text-[#171717] text-right">{buildingPractice.type || '—'}{buildingPractice.status === 'validata' ? ' · Validata' : ''}</span>
                {buildingPractice.number && (<><span className="text-[13px] text-[#737373]">Protocollo</span><span className="text-[14px] font-semibold text-[#171717] text-right">{buildingPractice.number}</span></>)}
                <span className="text-[13px] text-[#737373]">IVA lavori</span><span className="text-[14px] font-semibold text-[#171717] text-right">{project.vat_rate ?? 10}%</span>
              </div>
              <button onClick={() => setShowPraticaDialog(true)} data-testid="goto-pratica-dettagli"
                className="mt-2.5 text-[13px] text-[#FF7819] flex items-center gap-1 hover:underline">
                Vedi dettagli pratica <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Riga 3 — Ultimi SAL (2/3) + Variazioni da verificare (1/3) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
            <div className="lg:col-span-2 rounded-xl border border-[#E4E4E4] bg-white shadow-sm p-4" data-testid="panoramica-ultimi-sal">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[15px] font-semibold text-[#171717]">Ultimi SAL</h4>
                <button onClick={() => goToArea('lavori', 'sal')} className="text-[13px] text-[#FF7819] flex items-center gap-1 hover:underline" data-testid="goto-sal">
                  Vedi tutti i SAL <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              {(salReports || []).length === 0 ? (
                <p className="text-[14px] text-[#525252]">Nessun report SAL generato.</p>
              ) : (
                <div className="space-y-2">
                  {(salReports || []).slice(0, 3).map(r => (
                    <div key={r.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-[#171717]">{formatPercentage(r.sal_percentage || 0)}</span>
                        <span className="text-[#737373] text-[13px]">{r.created_at ? formatDateTime(r.created_at) : ''}</span>
                      </div>
                      {r.signed_at || r.signed_pdf_url
                        ? <span className="text-[12px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Firmato</span>
                        : <span className="text-[12px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Da firmare</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* STRALCIO GOVERNANCE · card variazioni da verificare (1/3) — logica invariata */}
            <VariationReviewCard
              projectId={id}
              canManage={isAdmin || user?.role === 'project_manager'}
              onChanged={loadData}
            />
          </div>

          {/* Riga 3b — Azioni rapide (full-width) */}
          <div className="grid grid-cols-1 gap-4 items-start">
            <div className="rounded-xl border border-[#E4E4E4] bg-white shadow-sm p-4" data-testid="panoramica-azioni-rapide">
              <h4 className="text-[15px] font-semibold text-[#171717] mb-2">Azioni rapide</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Button variant="outline" size="sm" onClick={() => goToArea('lavori', 'sal')} data-testid="qa-avanzamento" className="justify-start h-10 text-[14px] font-medium text-[#3C3C3C] border-[#D4D4D4] hover:border-[#FF7819] hover:bg-[#FFF7F0] hover:text-[#FF7819]">
                  <ListChecks className="w-[18px] h-[18px] mr-2 text-[#FF7819]" /> Avanzamento
                </Button>
                {project?.status !== 'chiuso' && (
                  <Button variant="outline" size="sm" onClick={() => goToArea('lavori', 'extras')} data-testid="qa-extra" className="justify-start h-10 text-[14px] font-medium text-[#3C3C3C] border-[#D4D4D4] hover:border-[#FF7819] hover:bg-[#FFF7F0] hover:text-[#FF7819]">
                    <Plus className="w-[18px] h-[18px] mr-2 text-[#FF7819]" /> Extra / Variazione
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => goToArea('documenti')} data-testid="qa-doc" className="justify-start h-10 text-[14px] font-medium text-[#3C3C3C] border-[#D4D4D4] hover:border-[#FF7819] hover:bg-[#FFF7F0] hover:text-[#FF7819]">
                  <Upload className="w-[18px] h-[18px] mr-2 text-[#FF7819]" /> Documenti
                </Button>
                <Button variant="outline" size="sm" onClick={() => goToArea('lavori', 'sitelog')} data-testid="qa-diario" className="justify-start h-10 text-[14px] font-medium text-[#3C3C3C] border-[#D4D4D4] hover:border-[#FF7819] hover:bg-[#FFF7F0] hover:text-[#FF7819]">
                  <History className="w-[18px] h-[18px] mr-2 text-[#FF7819]" /> Diario
                </Button>
              </div>
            </div>
          </div>

          {/* Gestione cantiere guidata (riuso componente esistente) */}

      {/* Global Progress - Lavori e Forniture */}
      <ProjectProgressCard
        lavoriPercentage={lavoriPercentage}
        lavoriCompletedValue={lavoriCompletedValueNet}
        lavoriTotalValue={lavoriTotalValueNet}
        forniturePercentage={forniturePercentage}
        fornitureDeliveredValue={fornitureDeliveredValue}
        fornitureTotalValue={fornitureTotalValue}
        hasForniture={fornitureItems.length > 0}
        showValues={canViewEconomic}
      />

      {/* Gestione Cantiere + Dati fiscali/IVA (IVA/contratto gated economico) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
        <div className={canViewEconomic ? "lg:col-span-2 h-full" : "lg:col-span-3 h-full"}>
          <ProjectSiteActionsCard
            projectId={id}
            onStatusChange={loadData}
            refreshTrigger={siteActionsRefreshKey}
          />
        </div>
        {canViewEconomic && (
        <div className="lg:col-span-1 h-full">
          <VatRateCard
            vatRate={project.vat_rate ?? 10}
            contractAmount={project.contract_amount ?? 0}
            needsReview={project._vat_needs_review === true}
            reviewReasons={project._vat_review_reasons || []}
            readOnly={!!project.contract_id}
            canEdit={isAdmin || isAdministration}
            source="project"
            viewModeFirst={true}
            title="Dati fiscali / IVA"
            onSave={async (newRate) => {
              const response = await projectApi.updateVatRate(id, newRate);
              setProject(response.data);
            }}
          />
        </div>
        )}
      </div>

      {/* Pratica Edilizia — dettaglio in drawer/modal (sintesi in Stato cantiere) */}
      <Dialog open={showPraticaDialog} onOpenChange={setShowPraticaDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pratica-dialog">
          <DialogHeader>
            <DialogTitle>Pratica Edilizia / Dati per Fatturazione</DialogTitle>
          </DialogHeader>
      {/* Pratica Edilizia Card */}
      <Card className="border-[#DCDCDC] mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#FF7819]" />
              <CardTitle className="text-base">Pratica Edilizia / Dati per Fatturazione</CardTitle>
              <Badge className={`text-xs ${
                buildingPractice.status === 'validata' ? 'bg-green-100 text-green-700 border-green-300' :
                buildingPractice.status === 'compilata' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                buildingPractice.status === 'non_necessaria' ? 'bg-gray-100 text-gray-600 border-gray-300' :
                'bg-amber-100 text-amber-700 border-amber-300'
              }`}>
                {buildingPractice.status === 'validata' ? 'Validata' :
                 buildingPractice.status === 'compilata' ? 'Compilata' :
                 buildingPractice.status === 'non_necessaria' ? 'Non necessaria' :
                 'Da compilare'}
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBuildingPracticeDialog(true)}
              className="text-[#FF7819] border-[#FF7819] hover:bg-[#FF7819]/10"
            >
              <Edit className="w-4 h-4 mr-1" />
              {buildingPractice.status === 'da_compilare' ? 'Compila' : 'Modifica'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {buildingPractice.status === 'da_compilare' ? (
            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
              <AlertTriangle className="w-5 h-5" />
              <p className="text-sm">Dati pratica edilizia da compilare per la fatturazione</p>
            </div>
          ) : buildingPractice.status === 'non_necessaria' ? (
            <div className="flex items-center gap-2 text-gray-600 bg-gray-50 p-3 rounded-lg">
              <CheckCircle className="w-5 h-5" />
              <p className="text-sm">Pratica edilizia non necessaria per questo intervento</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-[#737373]">Tipologia:</span>
                <span className="ml-2 font-medium">{buildingPractice.type || '-'}</span>
              </div>
              <div>
                <span className="text-[#737373]">Protocollo:</span>
                <span className="ml-2 font-medium">{buildingPractice.number || '-'}</span>
              </div>
              <div>
                <span className="text-[#737373]">Data:</span>
                <span className="ml-2 font-medium">{buildingPractice.date ? formatDate(buildingPractice.date) : '-'}</span>
              </div>
              <div>
                <span className="text-[#737373]">Comune:</span>
                <span className="ml-2 font-medium">{buildingPractice.municipality || '-'}</span>
              </div>
              <div>
                <span className="text-[#737373]">Tecnico:</span>
                <span className="ml-2 font-medium">{buildingPractice.technician || '-'}</span>
              </div>
              {buildingPractice.subject && (
                <div className="col-span-full">
                  <span className="text-[#737373]">Oggetto:</span>
                  <span className="ml-2">{buildingPractice.subject}</span>
                </div>
              )}
              {buildingPractice.invoice_notes && (
                <div className="col-span-full bg-gray-50 p-2 rounded">
                  <span className="text-[#737373] text-xs">Note per fattura:</span>
                  <p className="mt-1">{buildingPractice.invoice_notes}</p>
                </div>
              )}
            </div>
          )}
          {/* Pulsante Copia per fattura */}
          {buildingPractice.status !== 'da_compilare' && (
            <div className="mt-3 pt-3 border-t border-[#DCDCDC] flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  let text = '';
                  if (buildingPractice.status === 'non_necessaria') {
                    text = 'Pratica edilizia: non necessaria per il presente intervento.';
                  } else {
                    text = `Pratica edilizia: ${buildingPractice.type || ''} n. ${buildingPractice.number || ''} del ${buildingPractice.date ? formatDate(buildingPractice.date) : ''} - Comune di ${buildingPractice.municipality || ''}.`;
                    if (buildingPractice.subject) {
                      text += `\nOggetto intervento: ${buildingPractice.subject}.`;
                    }
                  }
                  navigator.clipboard.writeText(text);
                  toast.success('Dati copiati negli appunti');
                }}
                className="text-[#737373]"
              >
                <Copy className="w-4 h-4 mr-1" />
                Copia dati fattura
              </Button>
              {(isAdmin || isAdministration) && buildingPractice.status === 'compilata' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={async () => {
                    try {
                      await projectApi.validateBuildingPractice(id);
                      setBuildingPractice(prev => ({ ...prev, status: 'validata' }));
                      toast.success('Pratica edilizia validata');
                    } catch (error) {
                      toast.error('Errore nella validazione');
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Valida
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
        </DialogContent>
      </Dialog>

      {/* Tabs contenuti (navigazione gestita dal macro/sub-nav sopra) */}
        </TabsContent>

        {/* SAL Tab */}
        <TabsContent value="sal" className="space-y-4">
          {/* Header: titolo + toolbar azioni (Direttore Lavori rimosso — già nell'header progetto) */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-h4">Stato Avanzamento Lavori e Forniture</h3>
            {canViewEconomic && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={handleSyncExtras} data-testid="sync-extras-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Sincronizza Extra
              </Button>
              <Button variant="outline" onClick={() => setShowSalHistoryDialog(true)} data-testid="sal-history-btn">
                <History className="w-4 h-4 mr-2" />
                Storico SAL
              </Button>
              <Button onClick={handleGenerateSalReport} data-testid="generate-sal-report-btn" disabled={hasSalChanges}>
                <Download className="w-4 h-4 mr-2" />
                Genera Report SAL
              </Button>
            </div>
            )}
          </div>

          {/* TOP SUMMARY — 3 card equivalenti (§C): SAL Principale · Extra · Forniture.
              Consumer PASSIVO di /sal-summary. Nessun ricalcolo client. */}
          {salSummary?.principal_progress ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch" data-testid="avanzamento-summary">
              {/* CARD 1 — SAL PRINCIPALE */}
              <div className="rounded-xl border border-[#EDEDED] bg-white shadow-sm p-4 flex flex-col h-full" data-testid="summary-card-sal">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#737373]">SAL Principale</p>
                {salSummary.principal_progress.active_scope_weight > 0 ? (
                  <>
                    <p className="text-3xl font-bold text-[#FF7819] leading-tight mt-1" data-testid="sal-principale-pct">{formatPercentage(salSummary.principal_progress.percentage, 2)}</p>
                    <Progress value={salSummary.principal_progress.percentage || 0} className="h-2 mt-2" />
                  </>
                ) : (
                  <p className="text-3xl font-bold text-[#a3a3a3] leading-tight mt-1" data-testid="sal-principale-unavailable">—</p>
                )}
                <p className="text-xs text-[#737373] mt-auto pt-2">Avanzamento lavori contrattuali</p>
              </div>

              {/* CARD 2 — EXTRA */}
              <div className="rounded-xl border border-[#EDEDED] bg-white shadow-sm p-4 flex flex-col h-full" data-testid="summary-card-extra">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#737373]">Extra</p>
                {salSummary.extra_progress && salSummary.extra_progress.effective_extra_amount > 0 ? (
                  <>
                    <p className="text-3xl font-bold text-[#EAA300] leading-tight mt-1" data-testid="avanzamento-extra-pct">{formatPercentage(salSummary.extra_progress.percentage, 2)}</p>
                    <Progress value={salSummary.extra_progress.percentage || 0} className="h-2 mt-2 [&>div]:bg-[#EAA300]" />
                  </>
                ) : (
                  <p className="text-3xl font-bold text-[#a3a3a3] leading-tight mt-1" data-testid="avanzamento-extra-empty">—</p>
                )}
                <p className="text-xs text-[#737373] mt-auto pt-2">Avanzamento Extra accettati</p>
              </div>

              {/* CARD 3 — FORNITURE (consegnato / previsto, NO dati incasso) */}
              {fornitureItems.length > 0 ? (
                <div className="rounded-xl border border-[#EDEDED] bg-white shadow-sm p-4 flex flex-col h-full" data-testid="summary-card-forniture">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#737373]">Forniture</p>
                  <p className="text-3xl font-bold text-[#0078C8] leading-tight mt-1" data-testid="avanzamento-forniture-pct">
                    {formatPercentage(salSummary.forniture_progress?.percentage || 0, 2)} <span className="text-sm font-medium text-[#737373]">consegnato</span>
                  </p>
                  {canViewEconomic && (
                    <p className="text-sm font-medium text-[#525252] mt-0.5" data-testid="avanzamento-forniture-values">
                      {formatCurrency(salSummary.forniture_progress?.delivered_amount || 0)} / {formatCurrency(salSummary.forniture_progress?.total_amount || 0)}
                    </p>
                  )}
                  <Progress value={salSummary.forniture_progress?.percentage || 0} className="h-2 mt-2 [&>div]:bg-[#0078C8]" />
                  <div className="mt-auto pt-2">
                    <p className="text-xs text-[#737373]">Valore consegnato / previsto</p>
                    {(salSummary.forniture_progress?.total_supply_count || 0) > 0 && (
                      (salSummary.forniture_progress?.ordered_count || 0) > 0 ? (
                        <p className="text-xs text-[#525252] mt-1 flex items-center gap-1.5 flex-wrap" data-testid="forniture-order-summary">
                          <ShoppingCart className="w-3 h-3 text-[#0078C8] shrink-0" />
                          <span>
                            {canViewEconomic ? <span className="font-medium">Ordinato {formatCurrency(salSummary.forniture_progress.ordered_amount || 0)} · </span> : <span className="font-medium">Ordinato · </span>}
                            {salSummary.forniture_progress.ordered_count}/{salSummary.forniture_progress.total_supply_count} forniture
                          </span>
                        </p>
                      ) : (
                        <p className="text-xs text-[#a3a3a3] mt-1 flex items-center gap-1.5" data-testid="forniture-order-summary">
                          <ShoppingCart className="w-3 h-3 shrink-0" />
                          Da ordinare · 0/{salSummary.forniture_progress.total_supply_count} forniture
                        </p>
                      )
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-[#EDEDED] bg-[#FAFAFA] p-4 flex flex-col items-center justify-center h-full" data-testid="summary-card-forniture-empty">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#a3a3a3]">Forniture</p>
                  <p className="text-sm text-[#a3a3a3] mt-1">Nessuna fornitura</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-[#a3a3a3]" data-testid="avanzamento-summary-unavailable">Dati avanzamento non disponibili</p>
          )}

          {/* R.6.1 · Approvazione Cliente — sintesi compatta + dettaglio in dialog */}
          {(isAdmin || isAdministration || isProjectManager) && (
            <div className="rounded-xl border border-[#EDEDED] bg-white shadow-sm p-4 flex items-center justify-between gap-3" data-testid="sal-reports-compact">
              <div className="flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-[#FF7819]" />
                <div>
                  <p className="text-sm font-semibold text-[#171717]">Report SAL</p>
                  <p className="text-xs text-[#737373]">
                    {(salReports || []).length} disponibili · {(salReports || []).filter(r => !(r.signed_at || r.signed_pdf_url)).length} da firmare
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={() => setShowSalReportsDialog(true)} data-testid="manage-sal-reports-btn">
                Gestisci report SAL
              </Button>
            </div>
          )}

          {/* Dialog dettaglio Report SAL (lista completa + azioni preservate) */}
          <Dialog open={showSalReportsDialog} onOpenChange={setShowSalReportsDialog}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sal-reports-dialog">
              <DialogHeader>
                <DialogTitle>Report SAL · Approvazione Cliente</DialogTitle>
              </DialogHeader>
              <SalApprovalPanel projectId={id} salReports={salReports} project={project} />
            </DialogContent>
          </Dialog>
          
          {/* Barra salvataggio modifiche - Solo Admin e PM */}
          {hasSalChanges && (isAdmin || user?.role === 'project_manager') && (
            <Card className="card-abitalo bg-amber-50 border-amber-300">
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Hai modifiche non salvate</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleCancelSalChanges}>
                    Annulla
                  </Button>
                  <Button size="sm" className="btn-primary" onClick={handleSaveSalChanges} disabled={saving}>
                    <Save className="w-4 h-4 mr-1" />
                    {saving ? 'Salvataggio...' : 'Salva modifiche'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* SEZIONE LAVORI */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pt-2">
              <TrendingUp className="w-5 h-5 text-[#FF7819]" />
              <h4 className="text-lg font-semibold">Avanzamento Lavori</h4>
              <Badge variant="outline" className="ml-auto">{lavoriItems.length} voci</Badge>
            </div>
            
            {Object.entries(groupedLavoriItems).sort((a, b) => a[0].localeCompare(b[0])).map(([category, items]) => {
              // Filtra solo voci attive per il calcolo totali
              const activeItems = items.filter(i => i.item_status !== 'non_eseguita');
              // V1 FASE 1: applica contract_coefficient SOLO alle voci source='contratto'.
              // Le voci source='extra' restano invariate (regola per singolo progetto).
              const coefFor = (it) => (it.source === 'contratto' ? contractCoefficient : 1);
              const categoryTotal = activeItems.reduce(
                (sum, item) => sum + ((item.total_value || 0) * coefFor(item)),
                0
              );
              const categoryCompleted = activeItems.reduce((sum, item) => {
                const pct = getSalItemValue(item, 'completion_percentage') || 0;
                return sum + ((item.total_value || 0) * coefFor(item) * pct / 100);
              }, 0);
              const categoryPercentage = categoryTotal > 0 ? (categoryCompleted / categoryTotal) * 100 : 0;
              
              return (
                <Card key={category} className="card-abitalo">
                  <CardHeader className="pb-1.5">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">{categoryLabels[category] || category}</CardTitle>
                      <div className="text-right">
                        <span className="text-sm font-semibold text-[#FF7819]">{formatPercentage(categoryPercentage)}</span>
                        <p className="text-xs text-[#737373]">{formatCurrency(categoryCompleted)} / {formatCurrency(categoryTotal)}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {items.map(item => {
                      const currentPct = getSalItemValue(item, 'completion_percentage') || 0;
                      const isModified = salEdits[item.id]?.completion_percentage !== undefined;
                      const isExcluded = item.item_status === 'non_eseguita';
                      
                      return (
                        <div 
                          key={item.id} 
                          className={`p-2.5 rounded-lg ${
                            isExcluded 
                              ? 'bg-gray-100 border border-gray-300 opacity-60' 
                              : isModified 
                                ? 'bg-amber-50 border border-amber-200' 
                                : 'bg-[#FAFAFA]'
                          }`} 
                          data-testid={`sal-item-${item.id}`}
                        >
                          <div className="flex items-start justify-between mb-1.5">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs font-mono px-2 py-0.5 rounded ${isExcluded ? 'bg-gray-200 text-gray-500 line-through' : 'bg-[#E5F4FF] text-[#0078C8]'}`}>
                                  {item.code}
                                </span>
                                {isExcluded && (
                                  <span
                                    className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium"
                                    title={item.exclusion_reason ? `Motivo: ${item.exclusion_reason}` : ''}
                                    data-testid={`sal-item-stralciata-badge-${item.id}`}
                                  >
                                    STRALCIATA{item.stralcio_scope === 'residual' ? ' (residuo)' : ''}
                                  </span>
                                )}
                                {!isExcluded && item.source === 'extra' && (() => {
                                  const eff = isExtraEffective(extrasById[item.extra_id]);
                                  const label = eff ? 'EXTRA LAVORI' : 'EXTRA NON ATTIVO';
                                  const cls = eff
                                    ? 'bg-[#FFF0E5] text-[#FF7819] hover:bg-[#FFE0CC]'
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200';
                                  const title = eff ? 'Extra accettato dal cliente' : 'Extra non ancora accettato dal cliente';
                                  return item.extra_id ? (
                                    <Link
                                      to={`/projects/${id}/extras/${item.extra_id}`}
                                      className={`text-xs px-2 py-0.5 rounded font-medium transition-colors ${cls}`}
                                      title={title}
                                      data-testid={`sal-item-extra-badge-${item.id}`}
                                      data-extra-effective={eff ? 'true' : 'false'}
                                    >
                                      {label} →
                                    </Link>
                                  ) : (
                                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${cls}`} title={title} data-testid={`sal-item-extra-badge-${item.id}`} data-extra-effective={eff ? 'true' : 'false'}>
                                      {label}
                                    </span>
                                  );
                                })()}
                                {!isExcluded && isModified && (
                                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded font-medium">
                                    MODIFICATO
                                  </span>
                                )}
                              </div>
                              <p className={`text-sm font-medium mt-1 ${isExcluded ? 'line-through text-gray-500' : ''}`}>{item.description}</p>
                              {isExcluded && item.exclusion_reason && (
                                <p className="text-xs text-red-600 mt-1 italic">
                                  Motivo: {item.exclusion_reason}
                                </p>
                              )}
                              {isExcluded && item.excluded_by_name && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Esclusa da {item.excluded_by_name} il {item.excluded_at ? new Date(item.excluded_at).toLocaleDateString('it-IT') : '-'}
                                </p>
                              )}
                              {!isExcluded && (
                                <div className="flex items-center gap-4 text-xs text-[#737373] mt-1">
                                  <span>{((item.total_quantity * currentPct) / 100).toFixed(2)} / {item.total_quantity} {item.unit}</span>
                                  <span>•</span>
                                  <span>{formatCurrency((item.total_value || 0) * (item.source === 'contratto' ? contractCoefficient : 1))}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* R.5.2 §3 — Storia lavorazione (sempre disponibile a chi vede il progetto) */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setTimelineItemId(item.id)}
                                title="Storia lavorazione"
                                className="h-7 px-2 text-[#737373] hover:text-purple-700 hover:bg-purple-50"
                                data-testid={`sal-timeline-open-btn-${item.id}`}
                              >
                                <Clock className="w-4 h-4" />
                              </Button>
                              {/* Pulsante Escludi/Riattiva */}
                              {canExcludeSalItems && (
                                isExcluded ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleReactivateItem(item)}
                                    title="Riattiva voce"
                                    className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenExcludeModal(item)}
                                    title="Stralcia lavorazione"
                                    className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )
                              )}
                              {!isExcluded && (
                                <span className="text-sm font-semibold text-[#FF7819] ml-2">
                                  {formatPercentage(currentPct)}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* SAL progress control (5B-2a): Percentuale | Misura - Solo Admin/PM, nascosto per voci escluse */}
                          {!isExcluded && (isAdmin || user?.role === 'project_manager') ? (
                            <SalItemProgressControl
                              item={item}
                              currentPct={currentPct}
                              onPercentageChange={handleUpdateSalItemLocal}
                              onQuantitySaved={loadData}
                            />
                          ) : !isExcluded ? (
                            <div className="flex items-center gap-2 max-w-[560px]">
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-[#FF7819] transition-all"
                                  style={{ width: `${currentPct}%` }}
                                />
                              </div>
                              <span className="text-xs text-[#737373]">{formatPercentage(currentPct)}</span>
                            </div>
                          ) : null}
                          {/* Fase B/C · dettaglio misure operativo (fonte unica avanzamento) */}
                          <SalMeasureDetail
                            item={item}
                            canEdit={!isExcluded && (isAdmin || user?.role === 'project_manager')}
                            onSaved={loadData}
                          />
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })}
            
            {lavoriItems.length === 0 && (
              <Card className="card-abitalo">
                <CardContent className="p-6 text-center text-[#737373]">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nessuna voce lavori nel SAL</p>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* SEZIONE FORNITURE E MATERIALI (F, G, H) */}
          <div className="space-y-4 pt-4 border-t border-[#DCDCDC]">
            <div className="flex items-center gap-2 flex-wrap">
              <Truck className="w-5 h-5 text-[#0078C8]" />
              <h4 className="text-lg font-semibold">Forniture e Materiali</h4>
              <span className="text-xs text-[#737373]">(Finiture, Serramenti, Prodotti)</span>
              <Badge variant="outline" className="ml-auto">{fornitureItems.length} voci</Badge>
            </div>
            
            {/* Barra azioni multi-selezione - Solo Admin e PM */}
            {getSelectableSupplyItems().length > 0 && (isAdmin || user?.role === 'project_manager') && (
              <Card className="card-abitalo bg-blue-50 border-blue-200">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSupplyItems.size === getSelectableSupplyItems().length && selectedSupplyItems.size > 0}
                          onChange={toggleSelectAllSupplyItems}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-blue-800">
                          {selectedSupplyItems.size === 0 
                            ? 'Seleziona elementi da ordinare' 
                            : `${selectedSupplyItems.size} elementi selezionati`}
                        </span>
                      </label>
                    </div>
                    {selectedSupplyItems.size > 0 && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedSupplyItems(new Set())}
                          className="text-gray-600"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Annulla
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleOpenBulkOrderDialog}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Registra Ordine ({selectedSupplyItems.size})
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {Object.entries(groupedFornitureItems).sort((a, b) => a[0].localeCompare(b[0])).map(([category, items]) => {
              const categoryTotal = items.reduce((sum, item) => sum + (item.total_value || 0), 0);
              const categoryDelivered = items.reduce((sum, item) => {
                const delivered = getSalItemValue(item, 'delivered');
                return sum + (delivered ? (item.total_value || 0) : 0);
              }, 0);
              const categoryPercentage = categoryTotal > 0 ? (categoryDelivered / categoryTotal) * 100 : 0;
              
              return (
                <Card key={category} className="card-abitalo">
                  <CardHeader className="pb-1.5">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium">{categoryLabels[category] || category}</CardTitle>
                      <div className="text-right">
                        <span className="text-sm font-semibold text-[#0078C8]">{formatPercentage(categoryPercentage)}</span>
                        <p className="text-xs text-[#737373]">{formatCurrency(categoryDelivered)} / {formatCurrency(categoryTotal)}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {items.map(item => {
                      const isOrdered = getSalItemValue(item, 'ordered');
                      const isDelivered = getSalItemValue(item, 'delivered');
                      const isModified = salEdits[item.id]?.ordered !== undefined || salEdits[item.id]?.delivered !== undefined;
                      const orderSupplier = getSalItemValue(item, 'order_supplier');
                      const orderNumber = getSalItemValue(item, 'order_number');
                      const orderDate = getSalItemValue(item, 'order_date');
                      const isSelectable = !isOrdered;
                      const isSelected = selectedSupplyItems.has(item.id);
                      
                      return (
                        <div 
                          key={item.id} 
                          className={`p-2.5 rounded-lg ${
                            isSelected ? 'bg-blue-50 border border-blue-300' :
                            isModified ? 'bg-amber-50 border border-amber-200' : 
                            'bg-[#FAFAFA]'
                          }`} 
                          data-testid={`sal-fornitura-${item.id}`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox per selezione - Solo Admin e PM */}
                            {isSelectable && (isAdmin || user?.role === 'project_manager') && (
                              <div className="pt-1">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSupplyItemSelection(item.id)}
                                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                />
                              </div>
                            )}
                            
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-mono bg-[#E5F4FF] text-[#0078C8] px-2 py-0.5 rounded">
                                  {item.code}
                                </span>
                                {item.source === 'extra' && item.extra_id && (
                                  <Link 
                                    to={`/projects/${id}/extras/${item.extra_id}`}
                                    className="text-xs bg-[#FFF0E5] text-[#FF7819] px-2 py-0.5 rounded font-medium hover:bg-[#FFE0CC] transition-colors"
                                  >
                                    EXTRA FORNITURA →
                                  </Link>
                                )}
                                {item.source === 'extra' && !item.extra_id && (
                                  <span className="text-xs bg-[#FFF0E5] text-[#FF7819] px-2 py-0.5 rounded font-medium">
                                    EXTRA
                                  </span>
                                )}
                                {isModified && (
                                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded font-medium">
                                    MODIFICATO
                                  </span>
                                )}
                                {isSelected && (
                                  <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded font-medium">
                                    SELEZIONATO
                                  </span>
                                )}
                              </div>
                              <p className="text-sm font-medium mt-1">{item.description}</p>
                              <div className="flex items-center gap-4 text-xs text-[#737373] mt-1">
                                <span>Qtà: {item.total_quantity} {item.unit}</span>
                                <span>•</span>
                                <span>{formatCurrency(item.total_value || 0)}</span>
                              </div>
                              
                              {/* Info ordine se presente */}
                              {isOrdered && orderSupplier && (
                                <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200 text-xs">
                                  <div className="flex items-center gap-1 text-amber-800 font-medium">
                                    <ShoppingCart className="w-3 h-3" />
                                    Ordinato: {orderSupplier}
                                  </div>
                                  {orderNumber && <span className="text-amber-700">Rif: {orderNumber}</span>}
                                  {orderDate && <span className="text-amber-600 ml-2">del {formatDate(orderDate)}</span>}
                                </div>
                              )}
                            </div>
                            
                            {/* Checkbox Ordinato e Consegnato - Solo Admin e PM possono modificare */}
                            {(isAdmin || user?.role === 'project_manager') ? (
                              <div className="flex flex-col gap-2 ml-4">
                                {/* Checkbox Ordinato */}
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    id={`ordered-${item.id}`}
                                    checked={isOrdered || false}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        handleOpenOrderDialog(item);
                                      } else {
                                        handleCancelOrder(item.id);
                                      }
                                    }}
                                    className="h-5 w-5"
                                  />
                                  <Label 
                                    htmlFor={`ordered-${item.id}`} 
                                    className={`text-sm cursor-pointer ${isOrdered ? 'text-amber-600 font-medium' : 'text-[#737373]'}`}
                                  >
                                    {isOrdered ? 'Ordinato' : 'Da ordinare'}
                                  </Label>
                                </div>
                                
                                {/* Checkbox Consegnato - abilitato solo se ordinato */}
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    id={`delivered-${item.id}`}
                                    checked={isDelivered || false}
                                    disabled={!isOrdered && !item.ordered}
                                    onCheckedChange={(checked) => handleUpdateSalItemLocal(item.id, 'delivered', checked)}
                                    className="h-5 w-5"
                                  />
                                  <Label 
                                    htmlFor={`delivered-${item.id}`} 
                                    className={`text-sm cursor-pointer ${getDeliveryLabelClass(isDelivered, isOrdered, item.ordered)}`}
                                  >
                                    {isDelivered ? 'Consegnato' : 'Da consegnare'}
                                  </Label>
                                </div>
                              </div>
                            ) : (
                              /* Solo visualizzazione stato per Administration */
                              <div className="flex flex-col gap-1 ml-4 text-sm">
                                <span className={isOrdered ? 'text-amber-600 font-medium' : 'text-gray-400'}>
                                  {isOrdered ? '✓ Ordinato' : '○ Da ordinare'}
                                </span>
                                <span className={isDelivered ? 'text-green-600 font-medium' : 'text-gray-400'}>
                                  {isDelivered ? '✓ Consegnato' : '○ Da consegnare'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })}
            
            {fornitureItems.length === 0 && (
              <Card className="card-abitalo">
                <CardContent className="p-6 text-center text-[#737373]">
                  <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nessuna voce di Finiture, Serramenti o Prodotti nel SAL</p>
                  <p className="text-xs mt-1">Le voci delle categorie F, G, H appariranno qui</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <FatturazioneV2
            milestones={milestones}
            milestonesLoading={milestonesLoading}
            unlockStatus={unlockStatus}
            extraForniturePayments={extraForniturePayments}
            isAdmin={isAdmin}
            isAdministration={isAdministration}
            efMarkingGoodsReady={efMarkingGoodsReady}
            efMarkingPaid={efMarkingPaid}
            onRegenerateMilestones={handleRegenerateMilestones}
            onUpdateMilestoneStatus={handleUpdateMilestoneStatus}
            onGenerateInvoiceNotice={handleGenerateInvoiceNotice}
            onEfMarkGoodsReady={handleEfMarkGoodsReady}
            onDownloadEfAvviso={handleDownloadEfAvviso}
            onOpenEfInvoiceDialog={handleOpenEfInvoiceDialog}
            onEfMarkPaid={handleEfMarkPaid}
          />
        </TabsContent>

        {/* Extras Tab (Variazioni) */}
        <TabsContent value="extras" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-h4">Extra e Variazioni</h3>
              <p className="text-sm text-[#737373]">Scelte cliente/finiture, lavorazioni aggiuntive e riduzioni rispetto al contratto</p>
            </div>
          </div>

          {/* Superficie Variazioni V2 (STEP 4D + 4E.2): Scelte cliente/Finiture + Varianti + movimenti standalone */}
          <ContractVariationsTab
            projectId={id}
            extras={extras}
            salItems={salItems}
            canManageVariations={isAdmin || isAdministration}
            canReadVariations={isAdmin || isAdministration || isProjectManager}
            canEditStandalone={isAdmin || user?.role === 'project_manager'}
            canViewEconomic={canViewEconomic}
            canOperateFinishes={isAdmin || isAdministration || isProjectManager || isTechnician}
            onNewSelection={() => setShowRequestExtraModal(true)}
            projectClosed={project?.status === 'chiuso'}
            onNewExtra={(type) => navigate(`/projects/${id}/extras/new${type ? `?type=${type}` : ''}`)}
            onOpenExtra={(extraId) => navigate(`/projects/${id}/extras/${extraId}`)}
            onChanged={loadData}
          />

        </TabsContent>

        {/* Suspensions Tab */}
        <TabsContent value="suspensions" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-h4">Sospensioni</h3>
            <Button variant="outline" onClick={() => setShowSuspensionModal(true)}>
              <Pause className="w-4 h-4 mr-2" />
              Richiedi Sospensione
            </Button>
          </div>

          {suspensions.length === 0 ? (
            <Card className="card-abitalo">
              <CardContent className="p-8 text-center">
                <Pause className="w-12 h-12 text-[#DCDCDC] mx-auto mb-3" />
                <p className="text-[#737373]">Nessuna sospensione</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {suspensions.map(suspension => (
                <Card key={suspension.id} className="card-abitalo">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{suspension.reason}</p>
                        <p className="text-sm text-[#737373]">
                          Dal {formatDate(suspension.start_date)}
                          {suspension.expected_end_date && ` al ${formatDate(suspension.expected_end_date)}`}
                        </p>
                        {suspension.notes && <p className="text-sm mt-1">{suspension.notes}</p>}
                      </div>
                      
                      <div className="text-right">
                        <span className={getBadgeClass(suspensionStatusColors[suspension.status])}>
                          {suspensionStatusLabels[suspension.status]}
                        </span>
                        
                        {isAdmin && suspension.status === 'richiesta_inviata' && (
                          <div className="mt-2 space-x-2">
                            <Button size="sm" onClick={() => handleApproveSuspension(suspension.id, true)}>
                              Approva
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleApproveSuspension(suspension.id, false)}>
                              Respingi
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Furniture/Arredi Tab - Nuovo componente stile Extra */}
        <TabsContent value="furniture" className="space-y-4">
          <ProjectFurnitureTab
            projectId={id}
            user={user}
            isAdmin={isAdmin}
            isAdministration={isAdministration}
            onCreateNew={() => setShowFurnitureModal(true)}
            onEdit={(quote) => handleEditFurnitureQuote(quote)}
          />
        </TabsContent>

        {/* Diario di Cantiere Tab */}
        <TabsContent value="sitelog" className="space-y-4">
          <SiteLogPanel 
            projectId={id} 
            isReadOnly={isAdministration}
          />
        </TabsContent>

        {/* Documenti Tab */}
        <TabsContent value="documents" className="space-y-4" data-testid="documents-tab-content">
          {project?.lead_id ? (
            <DocumentManager 
              customerId={project.lead_id}
              title="Documenti Progetto"
              showHeader={true}
            />
          ) : (
            <Card className="card-abitalo">
              <CardContent className="py-8 text-center">
                <p className="text-[#737373]">Nessun cliente associato a questo progetto</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab Subappalti — Admin + PM */}
        {(isAdmin || isProjectManager) && (
          <TabsContent value="subcontracts" className="space-y-4" data-testid="subcontracts-tab-content">
            <ProjectSubcontractsTab project={project} onNavigateTab={setActiveTab} />
          </TabsContent>
        )}

        {/* Tab Quadro Economico - Solo Admin e Administration */}
        {(isAdmin || isAdministration) && (
          <TabsContent value="economic" className="space-y-4" data-testid="economic-tab-content">
            {/* Riepilogo economico sintetico (source-of-truth esistenti) */}
            <div className="rounded-xl border border-[#EDEDED] bg-white shadow-sm overflow-hidden" data-testid="economico-riepilogo">
              <div className="px-4 py-2.5 border-b border-[#EFEFEF] bg-[#FAFAFA]">
                <h3 className="text-sm font-semibold text-[#171717]">Riepilogo economico</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-[#F0F0F0]">
                <div className="p-4"><p className="text-xs text-[#8a8a8a]">Contratto originario</p><p className="text-lg font-semibold text-[#171717]">{formatCurrency(contractSummary?.initial_contract_value ?? project.contract_amount ?? 0)}</p></div>
                {(() => {
                  const posVar = (contractSummary?.positive_variations || 0) + (contractSummary?.variation_positive_total || 0);
                  const negVar = (contractSummary?.standalone_stralci_total || 0) + (contractSummary?.negative_variations_other_than_stralci || 0) + (contractSummary?.variation_negative_total || 0);
                  return (<>
                    <div className="p-4"><p className="text-xs text-emerald-600">Variazioni positive</p><p className="text-lg font-semibold text-emerald-700">{isZeroMoney(posVar) ? '' : '+'}{formatCurrency(posVar)}</p></div>
                    <div className="p-4"><p className="text-xs text-red-500">Variazioni negative</p><p className="text-lg font-semibold text-red-600">{isZeroMoney(negVar) ? '' : '−'}{formatCurrency(negVar)}</p></div>
                  </>);
                })()}
                <div className="p-4 bg-[#FFF7F0]"><p className="text-xs text-[#FF7819]">Valore lavori vigente</p><p className="text-lg font-bold text-[#FF7819]">{formatCurrency(contractSummary?.current_contract_value_computed ?? project.contract_amount ?? 0)}</p></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-y md:divide-y-0 divide-[#F0F0F0] border-t border-[#EFEFEF]">
                {(() => {
                  const fatturato = (milestones || []).filter(m => ['fatturato', 'incassato'].includes(m.status)).reduce((s, m) => s + (m.amount || 0), 0);
                  const incassato = (milestones || []).filter(m => m.status === 'incassato').reduce((s, m) => s + (m.amount || 0), 0);
                  const daFatturare = (milestones || []).filter(m => m.status === 'da_fatturare').reduce((s, m) => s + (m.amount || 0), 0);
                  const maturato = salSummary?.matured_amount ?? 0;
                  const vigente = contractSummary?.current_contract_value_computed ?? project.contract_amount ?? 0;
                  const residuo = Math.max(0, vigente - fatturato);
                  const cell = (label, val, cls = 'text-[#171717]') => (
                    <div className="p-4"><p className="text-xs text-[#8a8a8a]">{label}</p><p className={`text-lg font-semibold ${cls}`}>{formatCurrency(val)}</p></div>
                  );
                  return (<>
                    {cell('Maturato', maturato)}
                    {cell('Da fatturare', daFatturare, 'text-amber-600')}
                    {cell('Fatturato', fatturato)}
                    {cell('Incassato', incassato, 'text-emerald-700')}
                    {cell('Residuo', residuo, 'text-[#525252]')}
                  </>);
                })()}
              </div>
            </div>

            {/* Quadro Economico (dettaglio) */}
            <ProjectEconomicTab
              projectId={id}
              user={user}
              isAdmin={isAdmin}
              isAdministration={isAdministration}
            />
          </TabsContent>
        )}

        {/* Timeline Economica Tab */}
        {(isAdmin || isProjectManager || isAdministration) && (
          <TabsContent value="economic-timeline" className="space-y-4" data-testid="economic-timeline-tab-content">
            <ProjectEconomicTimelineTab projectId={id} />
          </TabsContent>
        )}
      </Tabs>

      {/* New Extra Modal */}
      <Dialog open={showExtraModal} onOpenChange={setShowExtraModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-h3">Nuovo Extra</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateExtra} className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select
                value={newExtra.extra_type}
                onValueChange={(value) => setNewExtra({ ...newExtra, extra_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lavoro">Lavoro (entra nel SAL)</SelectItem>
                  <SelectItem value="fornitura">Fornitura (50% + 50%)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Descrizione *</Label>
              <Input
                value={newExtra.description}
                onChange={(e) => setNewExtra({ ...newExtra, description: e.target.value })}
                className="input-abitalo"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>Quantità *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newExtra.quantity}
                  onChange={(e) => setNewExtra({ ...newExtra, quantity: e.target.value })}
                  className="input-abitalo"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Unità *</Label>
                <Input
                  value={newExtra.unit}
                  onChange={(e) => setNewExtra({ ...newExtra, unit: e.target.value })}
                  className="input-abitalo"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Prezzo Unit. *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newExtra.unit_price}
                  onChange={(e) => setNewExtra({ ...newExtra, unit_price: e.target.value })}
                  className="input-abitalo"
                  required
                />
              </div>
            </div>

            <div className="p-3 bg-[#FAFAFA] rounded-lg">
              <p className="text-sm text-[#737373]">Totale:</p>
              <p className="text-lg font-semibold text-[#FF7819]">
                {formatCurrency((parseFloat(newExtra.quantity) || 0) * (parseFloat(newExtra.unit_price) || 0))}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={newExtra.notes}
                onChange={(e) => setNewExtra({ ...newExtra, notes: e.target.value })}
                className="input-abitalo"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowExtraModal(false)}>
                Annulla
              </Button>
              <Button type="submit" className="btn-primary">
                Crea Extra
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Suspension Modal */}
      <Dialog open={showSuspensionModal} onOpenChange={setShowSuspensionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-h3">Richiedi Sospensione</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSuspension} className="space-y-4">
            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Select
                value={newSuspension.reason}
                onValueChange={(value) => setNewSuspension({ ...newSuspension, reason: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona motivo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ritardi forniture">Ritardi forniture</SelectItem>
                  <SelectItem value="Extra in attesa approvazione">Extra in attesa approvazione</SelectItem>
                  <SelectItem value="Ritardo pagamenti cliente">Ritardo pagamenti cliente</SelectItem>
                  <SelectItem value="Problemi cantiere">Problemi cantiere</SelectItem>
                  <SelectItem value="Richiesta cliente">Richiesta cliente</SelectItem>
                  <SelectItem value="Altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Inizio *</Label>
                <Input
                  type="date"
                  value={newSuspension.start_date}
                  onChange={(e) => setNewSuspension({ ...newSuspension, start_date: e.target.value })}
                  className="input-abitalo"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fine Prevista</Label>
                <Input
                  type="date"
                  value={newSuspension.expected_end_date}
                  onChange={(e) => setNewSuspension({ ...newSuspension, expected_end_date: e.target.value })}
                  className="input-abitalo"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={newSuspension.notes}
                onChange={(e) => setNewSuspension({ ...newSuspension, notes: e.target.value })}
                className="input-abitalo"
                placeholder="Dettagli aggiuntivi..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSuspensionModal(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="btn-primary"
                disabled={!newSuspension.reason || !newSuspension.start_date}
              >
                Invia Richiesta
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Extra Accept Modal - Upload signed document */}
      <Dialog open={showExtraAcceptModal} onOpenChange={setShowExtraAcceptModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#FF7819]" />
              Accettazione Extra
            </DialogTitle>
            <DialogDescription>
              Carica il documento dell'extra firmato dal cliente e dal tecnico per confermare l'accettazione.
            </DialogDescription>
          </DialogHeader>
          
          {selectedExtraForAccept && (
            <div className="space-y-4 py-2">
              <div className="bg-[#FAFAFA] rounded-lg p-3">
                <p className="text-sm font-medium">{selectedExtraForAccept.extra_number}</p>
                <p className="text-sm text-[#737373]">{selectedExtraForAccept.title || 'Extra'}</p>
                <p className="text-lg font-semibold text-[#FF7819] mt-1">
                  {formatCurrency(selectedExtraForAccept.total)}
                </p>
              </div>
              
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                <input
                  type="file"
                  id="extra-signed-upload"
                  onChange={handleExtraSignedUpload}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                />
                
                {extraSignedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-700">{extraSignedFile.name}</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setExtraSignedFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </Button>
                  </div>
                ) : (
                  <label htmlFor="extra-signed-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto text-[#DCDCDC] mb-2" />
                    <p className="text-sm text-[#737373]">
                      Clicca per caricare il documento firmato
                    </p>
                    <p className="text-xs text-[#DCDCDC] mt-1">PDF, JPG, PNG - Max 10MB</p>
                  </label>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowExtraAcceptModal(false)}>
              Annulla
            </Button>
            <Button 
              className="btn-primary"
              onClick={handleClientAcceptExtra}
              disabled={!extraSignedFile || uploadingExtraSigned}
            >
              {uploadingExtraSigned ? 'Caricamento...' : 'Conferma Accettazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Storico SAL Dialog */}
      <Dialog open={showSalHistoryDialog} onOpenChange={setShowSalHistoryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#FF7819]" />
              Storico Report SAL
            </DialogTitle>
            <DialogDescription>
              Cronologia dei report SAL generati per questo progetto
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto space-y-3 py-2">
            {salReports.length === 0 ? (
              <div className="text-center py-8 text-[#737373]">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Nessun report SAL generato</p>
                <p className="text-xs mt-1">Genera il primo report dalla pagina SAL</p>
              </div>
            ) : (
              salReports.map((report, index) => (
                <div key={report.id} className="p-4 bg-[#FAFAFA] rounded-lg border border-[#DCDCDC]">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg font-bold text-[#FF7819]">
                          {formatPercentage(report.sal_percentage)}
                        </span>
                        {report.signed_pdf_url ? (
                          <Badge className="bg-green-100 text-green-700 border-green-300">Firmato</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-300">Da firmare</Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-[#737373]">
                        <User className="w-4 h-4" />
                        <span className="font-medium">{report.created_by_name}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-[#737373] mt-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatDateTime(report.created_at)}</span>
                      </div>
                      
                      {report.signed_at && (
                        <div className="flex items-center gap-2 text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3" />
                          <span>Firmato il {formatDateTime(report.signed_at)}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <Button variant="ghost" size="sm" className="h-8">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {index < salReports.length - 1 && report.sal_percentage !== salReports[index + 1]?.sal_percentage && (
                    <div className="mt-2 pt-2 border-t border-[#DCDCDC]">
                      <span className="text-xs text-[#737373]">
                        Variazione: +{(report.sal_percentage - (salReports[index + 1]?.sal_percentage || 0)).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSalHistoryDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* R.5.2 §3 — Modal Storia lavorazione */}
      <SalItemTimelineModal
        open={!!timelineItemId}
        onOpenChange={(o) => !o && setTimelineItemId(null)}
        salItemId={timelineItemId}
      />

      {/* R.6.0A — Modal Direttore Lavori */}
      <DirectorOfWorksDialog
        open={showDlDialog}
        onOpenChange={setShowDlDialog}
        project={project}
        onSaved={(saved) => setProject((prev) => prev ? { ...prev, ...saved } : prev)}
      />

      {/* STEP 4E.1B — Assegnazione Project Manager */}
      <ProjectManagerDialog
        open={showPmDialog}
        onOpenChange={setShowPmDialog}
        project={project}
        onSaved={loadData}
      />

      {/* Modal Stralcio Voce SAL (R.5) */}
      <Dialog open={showExcludeModal} onOpenChange={(open) => {
        if (!open) {
          setShowExcludeModal(false);
          setExcludingItem(null);
          setExcludeReason('');
          setExcludeCategory('');
          setExcludeEconomicEffect('');
          setExcludeReplacementExtraId('');
        }
      }}>
        <DialogContent className="max-w-lg" data-testid="stralcio-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" />
              Stralcia lavorazione
            </DialogTitle>
            <DialogDescription>
              Lo stralcio <strong>non elimina</strong> la lavorazione: la voce resta
              nello storico e nei report. Uno stralcio residuo preserva la parte già eseguita.
            </DialogDescription>
          </DialogHeader>

          {excludingItem && (() => {
            const contractQty = Number(excludingItem.total_quantity || 0);
            const unitPrice = Number(excludingItem.unit_price || 0);
            const executedQty = Number(excludingItem.completed_quantity || 0);
            const executedPct = Number(excludingItem.completion_percentage || 0);
            const executedAmount = Math.round(executedQty * unitPrice * 100) / 100;
            const residualQty = Math.max(0, contractQty - executedQty);
            const residualAmount = Math.round(residualQty * unitPrice * 100) / 100;
            const isPartial = executedQty > 0 || executedPct > 0;
            const scopeLabel = isPartial ? 'RESIDUO (la voce è parzialmente eseguita)' : 'TOTALE';

            return (
              <div className="py-2 space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-mono text-[#0078C8]" data-testid="stralcio-item-code">{excludingItem.code}</p>
                  <p className="text-sm font-medium mt-1">{excludingItem.description}</p>
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div>Q.tà contrattuale: <strong>{contractQty.toFixed(2)}</strong></div>
                    <div>Q.tà già eseguita: <strong>{executedQty.toFixed(2)}</strong></div>
                    <div>Q.tà residua: <strong>{residualQty.toFixed(2)}</strong></div>
                    <div>Avanzamento: <strong>{executedPct.toFixed(1)}%</strong></div>
                    <div>Valore già maturato: <strong>{formatCurrency(executedAmount)}</strong></div>
                    <div>Valore che verrà stralciato: <strong className="text-red-700">{formatCurrency(isPartial ? residualAmount : contractQty * unitPrice)}</strong></div>
                  </div>
                  <div className="mt-2 text-xs">
                    Scope: <span className={isPartial ? 'text-amber-700 font-semibold' : 'text-red-700 font-semibold'}>{scopeLabel}</span>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">
                    Categoria <span className="text-red-500">*</span>
                  </Label>
                  <Select value={excludeCategory} onValueChange={setExcludeCategory}>
                    <SelectTrigger className="mt-1" data-testid="stralcio-category-select">
                      <SelectValue placeholder="Seleziona una categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rinuncia_cliente">Rinuncia cliente</SelectItem>
                      <SelectItem value="impossibilita_tecnica">Impossibilità tecnica</SelectItem>
                      <SelectItem value="errore_progettuale">Errore progettuale</SelectItem>
                      <SelectItem value="sostituita_da_variante">Sostituita da variante</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {excludeCategory === 'sostituita_da_variante' && (
                  <div>
                    <Label className="text-sm font-medium">
                      Variante approvata collegata <span className="text-red-500">*</span>
                    </Label>
                    <Select value={excludeReplacementExtraId} onValueChange={setExcludeReplacementExtraId}>
                      <SelectTrigger className="mt-1" data-testid="stralcio-replacement-extra-select">
                        <SelectValue placeholder={approvedExtrasForReplacement.length ? 'Seleziona la variante' : 'Nessuna variante approvata disponibile'} />
                      </SelectTrigger>
                      <SelectContent>
                        {approvedExtrasForReplacement.map((ex) => (
                          <SelectItem key={ex.id} value={ex.id}>
                            {ex.title || ex.number || ex.id.slice(0, 8)} — {formatCurrency(Number(ex.total || ex.total_amount || 0))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-emerald-700 mt-1">Effetto contrattuale: recepito in variante (nessuna doppia riduzione).</p>
                  </div>
                )}

                {(excludeCategory === 'errore_progettuale' || excludeCategory === 'altro') && (
                  <div>
                    <Label className="text-sm font-medium">
                      Effetto contrattuale <span className="text-red-500">*</span>
                    </Label>
                    <Select value={excludeEconomicEffect} onValueChange={setExcludeEconomicEffect}>
                      <SelectTrigger className="mt-1" data-testid="stralcio-economic-effect-select">
                        <SelectValue placeholder="Seleziona l'effetto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contract_reduction">Riduzione del contratto</SelectItem>
                        <SelectItem value="represented_by_variation">Già recepito in variante</SelectItem>
                        <SelectItem value="no_contract_effect">Nessun effetto contrattuale</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="exclude_reason" className="text-sm font-medium">
                    Nota / motivazione <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="exclude_reason"
                    value={excludeReason}
                    onChange={(e) => setExcludeReason(e.target.value)}
                    placeholder="Descrivi la motivazione dello stralcio (min 8 caratteri)…"
                    className="mt-2 min-h-[90px]"
                    data-testid="stralcio-reason-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">{excludeReason.length}/8+ caratteri</p>
                </div>

                <div className="text-xs text-amber-800 bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <AlertTriangle className="w-4 h-4 inline mr-2 -mt-0.5" />
                  Lo stralcio non elimina la lavorazione. La voce resterà nello storico e nei report.
                  {isPartial ? ' La parte già eseguita rimane contabilizzata.' : ''}
                </div>
              </div>
            );
          })()}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowExcludeModal(false);
                setExcludingItem(null);
                setExcludeReason('');
                setExcludeCategory('');
                setExcludeEconomicEffect('');
                setExcludeReplacementExtraId('');
              }}
              data-testid="stralcio-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmExclude}
              disabled={excludingInProgress || !excludeReason.trim() || !excludeCategory}
              data-testid="stralcio-confirm-btn"
            >
              {excludingInProgress ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <Ban className="w-4 h-4 mr-2" />
                  Conferma stralcio
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      
      {/* Order Dialog per Forniture */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-amber-600" />
              Dettagli Ordine
            </DialogTitle>
            <DialogDescription>
              {orderingItem && (
                <span className="text-sm">{orderingItem.description}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="order_supplier">
                Fornitore <span className="text-red-500">*</span>
              </Label>
              <Input
                id="order_supplier"
                value={orderForm.order_supplier}
                onChange={(e) => setOrderForm(prev => ({ ...prev, order_supplier: e.target.value }))}
                placeholder="Nome fornitore"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="order_number">N° Ordine / Riferimento</Label>
                <Input
                  id="order_number"
                  value={orderForm.order_number}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, order_number: e.target.value }))}
                  placeholder="Es: ORD-001"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="order_date">Data Ordine</Label>
                <Input
                  id="order_date"
                  type="date"
                  value={orderForm.order_date}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, order_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="order_notes">Note</Label>
              <Textarea
                id="order_notes"
                value={orderForm.order_notes}
                onChange={(e) => setOrderForm(prev => ({ ...prev, order_notes: e.target.value }))}
                placeholder="Note sull'ordine..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowOrderDialog(false)}>
              Annulla
            </Button>
            <Button className="btn-primary" onClick={handleConfirmOrder}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Conferma Ordine
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Ordine Bulk */}
      <Dialog open={showBulkOrderDialog} onOpenChange={setShowBulkOrderDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
              Ordine Multiplo
            </DialogTitle>
            <DialogDescription>
              Stai registrando un ordine per <strong>{selectedSupplyItems.size} elementi</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Lista elementi selezionati */}
            <div className="max-h-32 overflow-y-auto bg-blue-50 rounded-lg p-2 border border-blue-200">
              <p className="text-xs font-medium text-blue-800 mb-1">Elementi selezionati:</p>
              <ul className="text-xs text-blue-700 space-y-0.5">
                {salItems?.filter(item => selectedSupplyItems.has(item.id)).map(item => (
                  <li key={item.id} className="truncate">• {item.code} - {item.description}</li>
                ))}
              </ul>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Fornitore *</Label>
              <Input
                value={orderForm.order_supplier}
                onChange={(e) => setOrderForm(prev => ({ ...prev, order_supplier: e.target.value }))}
                placeholder="Es: SCAVOLINI, OKNOPLAST..."
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">N° Ordine</Label>
                <Input
                  value={orderForm.order_number}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, order_number: e.target.value }))}
                  placeholder="ORD-2026-001"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Data Ordine</Label>
                <Input
                  type="date"
                  value={orderForm.order_date}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, order_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Note</Label>
              <Textarea
                value={orderForm.order_notes}
                onChange={(e) => setOrderForm(prev => ({ ...prev, order_notes: e.target.value }))}
                placeholder="Note sull'ordine..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowBulkOrderDialog(false);
              setSelectedSupplyItems(new Set());
            }}>
              Annulla
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleConfirmBulkOrder}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Conferma Ordine ({selectedSupplyItems.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Furniture Quote Modal */}
      <FurnitureQuoteForm
        isOpen={showFurnitureModal}
        onClose={() => {
          setShowFurnitureModal(false);
          setEditingFurnitureQuote(null);
        }}
        projectId={id}
        existingQuote={editingFurnitureQuote}
        onSaved={handleFurnitureQuoteSaved}
      />

      {/* Request Extra Modal (Technician) */}
      <Dialog open={showRequestExtraModal} onOpenChange={setShowRequestExtraModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-h3">Nuova scelta cliente</DialogTitle>
            <DialogDescription>
              Avvia la scelta finiture del cliente e fissa l'appuntamento presso il fornitore
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRequestExtra} className="space-y-4">
            <div className="space-y-2">
              <Label>Categoria scelta *</Label>
              <Select 
                value={requestExtraData.extra_category} 
                onValueChange={(v) => setRequestExtraData(prev => ({ ...prev, extra_category: v }))}
              >
                <SelectTrigger data-testid="request-extra-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="F">F - Finiture</SelectItem>
                  <SelectItem value="G">G - Serramenti</SelectItem>
                  <SelectItem value="H">H - Prodotti</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Descrizione *</Label>
              <Input
                value={requestExtraData.title}
                onChange={(e) => setRequestExtraData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Es: Scelta parquet presso showroom partner"
                className="input-abitalo"
                data-testid="request-extra-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Note richiesta</Label>
              <Textarea
                value={requestExtraData.notes}
                onChange={(e) => setRequestExtraData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Altre informazioni utili per l'amministrazione..."
                className="input-abitalo min-h-[60px]"
              />
            </div>
            
            {/* Sezione Appuntamento Scelte */}
            <div className="border-t border-[#DCDCDC] pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <CalendarIcon className="w-5 h-5 text-[#FF7819]" />
                <h4 className="font-semibold text-[#3C3C3C]">Appuntamento Scelte</h4>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <Input
                    type="date"
                    value={requestExtraData.choice_appointment_date}
                    onChange={(e) => setRequestExtraData(prev => ({ ...prev, choice_appointment_date: e.target.value }))}
                    className="input-abitalo"
                    data-testid="choice-appointment-date"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ora *</Label>
                  <Input
                    type="time"
                    value={requestExtraData.choice_appointment_time}
                    onChange={(e) => setRequestExtraData(prev => ({ ...prev, choice_appointment_time: e.target.value }))}
                    className="input-abitalo"
                    data-testid="choice-appointment-time"
                  />
                </div>
              </div>
              
              <div className="space-y-2 mt-3">
                <Label>Fornitore partner (anagrafica)</Label>
                <Select
                  value={requestExtraData.supplier_id || 'manual'}
                  onValueChange={(v) => {
                    if (v === 'manual') {
                      setRequestExtraData(prev => ({ ...prev, supplier_id: null }));
                      return;
                    }
                    const s = supplierOptions.find(o => o.id === v);
                    setRequestExtraData(prev => ({
                      ...prev,
                      supplier_id: v,
                      choice_supplier_name: s?.ragione_sociale || prev.choice_supplier_name,
                      choice_supplier_address: [s?.sede_legale, s?.sede_legale_city].filter(Boolean).join(', ') || prev.choice_supplier_address,
                    }));
                  }}
                >
                  <SelectTrigger data-testid="choice-supplier-picker">
                    <SelectValue placeholder="Seleziona un fornitore partner (opzionale)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Altro / inserimento manuale</SelectItem>
                    {supplierOptions.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.ragione_sociale}{s.sede_legale_city ? ` · ${s.sede_legale_city}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#a3a3a3]">Anagrafica incompleta? Lascia "Altro" e compila i campi sotto.</p>
              </div>

              <div className="space-y-2 mt-3">
                <Label>Fornitore *</Label>
                <Input
                  value={requestExtraData.choice_supplier_name}
                  onChange={(e) => setRequestExtraData(prev => ({ ...prev, choice_supplier_name: e.target.value }))}
                  placeholder="Es: Showroom Ceramiche Milano"
                  className="input-abitalo"
                  data-testid="choice-supplier-name"
                />
              </div>
              
              <div className="space-y-2 mt-3">
                <Label>Indirizzo fornitore</Label>
                <Input
                  value={requestExtraData.choice_supplier_address}
                  onChange={(e) => setRequestExtraData(prev => ({ ...prev, choice_supplier_address: e.target.value }))}
                  placeholder="Via Roma 123, Milano"
                  className="input-abitalo"
                  data-testid="choice-supplier-address"
                />
              </div>
              
              <div className="space-y-2 mt-3">
                <Label>Note appuntamento</Label>
                <Textarea
                  value={requestExtraData.choice_appointment_notes}
                  onChange={(e) => setRequestExtraData(prev => ({ ...prev, choice_appointment_notes: e.target.value }))}
                  placeholder="Es: Chiedere di Mario, referente commerciale..."
                  className="input-abitalo min-h-[60px]"
                />
              </div>
            </div>
            
            <div className="p-3 bg-[#FFF0E5] rounded-lg text-sm text-[#3C3C3C] border border-[#FF7819]/30">
              <strong>Notifiche automatiche:</strong> Verranno inviate email a:
              <ul className="mt-1 ml-4 list-disc text-[#737373]">
                <li>Tecnico assegnato</li>
                <li>Cliente</li>
                <li>Amministrazione</li>
              </ul>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRequestExtraModal(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="btn-primary"
                disabled={
                  requestingExtra || 
                  !requestExtraData.title.trim() ||
                  !requestExtraData.choice_appointment_date ||
                  !requestExtraData.choice_appointment_time ||
                  !requestExtraData.choice_supplier_name.trim()
                }
                data-testid="submit-request-extra-btn"
              >
                {requestingExtra ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Crea scelta cliente
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Extra Forniture - Segna Fatturato */}
      <Dialog open={showEfInvoiceDialog} onOpenChange={setShowEfInvoiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-600" />
              Registra Fattura Extra Forniture
            </DialogTitle>
            <DialogDescription>
              {selectedEfPayment?.name} - {selectedEfPayment?.extra_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <p className="text-sm text-teal-800">
                Importo da fatturare: <strong>{formatCurrency(selectedEfPayment?.amount)}</strong>
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ef-invoice-number">Numero Fattura *</Label>
              <Input
                id="ef-invoice-number"
                value={efInvoiceForm.invoice_number}
                onChange={(e) => setEfInvoiceForm({ ...efInvoiceForm, invoice_number: e.target.value })}
                placeholder="Es. FV-2024/001"
                data-testid="ef-invoice-number-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ef-invoice-date">Data Fattura *</Label>
                <Input
                  id="ef-invoice-date"
                  type="date"
                  value={efInvoiceForm.invoice_date}
                  onChange={(e) => setEfInvoiceForm({ ...efInvoiceForm, invoice_date: e.target.value })}
                  data-testid="ef-invoice-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ef-due-date">Scadenza</Label>
                <Input
                  id="ef-due-date"
                  type="date"
                  value={efInvoiceForm.due_date}
                  onChange={(e) => setEfInvoiceForm({ ...efInvoiceForm, due_date: e.target.value })}
                  data-testid="ef-due-date-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEfInvoiceDialog(false)}>
              Annulla
            </Button>
            <Button 
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleSaveEfInvoice}
              disabled={!efInvoiceForm.invoice_number || savingEfInvoice}
              data-testid="ef-save-invoice-btn"
            >
              {savingEfInvoice ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Conferma Fattura
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation Modal (Admin Only) */}
      <Dialog open={showDeleteModal} onOpenChange={(open) => {
        setShowDeleteModal(open);
        if (!open) {
          setDeleteConfirmText('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Elimina Progetto
            </DialogTitle>
            <DialogDescription>
              Questa azione è irreversibile. Verranno eliminati anche tutti i dati correlati:
              SAL, Extra, Sospensioni, Milestone, Report e Preventivi Arredi.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                Per confermare, digita il numero del progetto: <strong>{project?.project_number}</strong>
              </p>
            </div>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={project?.project_number}
              className="font-mono"
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteProject}
              disabled={deleteConfirmText !== project?.project_number || deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Eliminazione...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina Definitivamente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Pratica Edilizia */}
      <Dialog open={showBuildingPracticeDialog} onOpenChange={setShowBuildingPracticeDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Pratica Edilizia / Dati per Fatturazione</DialogTitle>
            <DialogDescription>
              Compila i dati della pratica edilizia necessari per la fatturazione
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipologia Pratica *</Label>
                <Select
                  value={buildingPractice.type || ''}
                  onValueChange={(value) => setBuildingPractice(prev => ({ 
                    ...prev, 
                    type: value,
                    required: value !== 'Non necessaria',
                    status: value === 'Non necessaria' ? 'non_necessaria' : prev.status === 'non_necessaria' ? 'da_compilare' : prev.status
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona tipologia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CILA">CILA</SelectItem>
                    <SelectItem value="SCIA">SCIA</SelectItem>
                    <SelectItem value="Permesso di Costruire">Permesso di Costruire</SelectItem>
                    <SelectItem value="CILAS">CILAS</SelectItem>
                    <SelectItem value="Edilizia libera">Edilizia libera</SelectItem>
                    <SelectItem value="Sanatoria">Sanatoria</SelectItem>
                    <SelectItem value="Altro">Altro</SelectItem>
                    <SelectItem value="Non necessaria">Non necessaria</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Numero Protocollo *</Label>
                <Input
                  value={buildingPractice.number || ''}
                  onChange={(e) => setBuildingPractice(prev => ({ ...prev, number: e.target.value }))}
                  placeholder="Es. 12345/2026"
                  disabled={buildingPractice.type === 'Non necessaria'}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Presentazione *</Label>
                <Input
                  type="date"
                  value={buildingPractice.date || ''}
                  onChange={(e) => setBuildingPractice(prev => ({ ...prev, date: e.target.value }))}
                  disabled={buildingPractice.type === 'Non necessaria'}
                />
              </div>
              <div className="space-y-2">
                <Label>Comune *</Label>
                <Input
                  value={buildingPractice.municipality || ''}
                  onChange={(e) => setBuildingPractice(prev => ({ ...prev, municipality: e.target.value }))}
                  placeholder="Es. Bari"
                  disabled={buildingPractice.type === 'Non necessaria'}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tecnico Incaricato</Label>
              <Input
                value={buildingPractice.technician || ''}
                onChange={(e) => setBuildingPractice(prev => ({ ...prev, technician: e.target.value }))}
                placeholder="Es. Ing. Mario Rossi"
                disabled={buildingPractice.type === 'Non necessaria'}
              />
            </div>
            <div className="space-y-2">
              <Label>Oggetto Intervento</Label>
              <Textarea
                value={buildingPractice.subject || ''}
                onChange={(e) => setBuildingPractice(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Es. Manutenzione straordinaria immobile sito in..."
                disabled={buildingPractice.type === 'Non necessaria'}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Note Fiscali per Fattura</Label>
              <Textarea
                value={buildingPractice.invoice_notes || ''}
                onChange={(e) => setBuildingPractice(prev => ({ ...prev, invoice_notes: e.target.value }))}
                placeholder="Note aggiuntive da riportare in fattura..."
                disabled={buildingPractice.type === 'Non necessaria'}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBuildingPracticeDialog(false)}>
              Annulla
            </Button>
            <Button
              onClick={async () => {
                try {
                  // FIX P0: validazione client-side campi obbligatori
                  // Il backend richiede type + number + date + municipality per
                  // marcare status='compilata'. Se manca uno di questi, il PUT
                  // riesce ma lo status resta 'da_compilare' senza feedback UI.
                  const isNonNecessaria = buildingPractice.type === 'Non necessaria';
                  if (!isNonNecessaria) {
                    const missing = [];
                    if (!buildingPractice.type) missing.push('Tipologia');
                    if (!buildingPractice.number) missing.push('Numero Protocollo');
                    if (!buildingPractice.date) missing.push('Data Presentazione');
                    if (!buildingPractice.municipality) missing.push('Comune');
                    if (missing.length > 0) {
                      toast.error(`Compila i campi obbligatori: ${missing.join(', ')}`);
                      return;
                    }
                  }
                  setSavingBuildingPractice(true);
                  const response = await projectApi.updateBuildingPractice(id, buildingPractice);
                  setBuildingPractice(response.data);
                  // Guard: se il backend torna 'da_compilare' significa che
                  // manca ancora qualcosa (fallback di sicurezza).
                  if (response.data?.status === 'da_compilare' && !isNonNecessaria) {
                    toast.warning('Pratica salvata parzialmente: alcuni campi obbligatori risultano mancanti.');
                  } else {
                    setShowBuildingPracticeDialog(false);
                    toast.success('Pratica edilizia salvata');
                  }
                  // Forza il reload di siteStatus nella card Gestione Cantiere
                  // (practice_status letto da endpoint separato /site-status)
                  setSiteActionsRefreshKey(k => k + 1);
                } catch (error) {
                  toast.error('Errore nel salvataggio');
                } finally {
                  setSavingBuildingPractice(false);
                }
              }}
              disabled={savingBuildingPractice}
              className="bg-[#FF7819] hover:bg-[#E56A10]"
              data-testid="building-practice-save-btn"
            >
              {savingBuildingPractice ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                'Salva'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetailPage;
